import React, { useState, useEffect, useMemo, useCallback, useRef, useReducer } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  CalendarIcon,
  ArrowPathIcon,
  UserIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  BanknotesIcon,
  PlayIcon,
  TrophyIcon,
  BuildingOfficeIcon,
  GlobeAsiaAustraliaIcon,
  BuildingStorefrontIcon,
  StarIcon,
  UserGroupIcon,
  ChevronRightIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  ShieldExclamationIcon
} from '@heroicons/react/24/outline';
import { format, subDays, startOfMonth, endOfMonth, startOfDay, endOfDay } from 'date-fns';
import { fetchUserTree, getCachedTree } from '../services/userTreeService';
import { fetchSelf, getSelfUsername, getSelfUserType } from '../services/userSelf';
import useTranslation from '../services/translationService';
import { apiPost } from '../utils/api';
import { fetchAuthSession } from '@aws-amplify/auth';
import Loading from '../components/Loading';
import LimitScopeModal from '../components/LimitScopeModal';
import { formatCurrency, createAmountTooltip } from '../utils/currency';
import { getGameNameWithFallback } from '../utils/gameNameMapping';
import { canManageLimits } from '../utils/limitScopes';
import {
  formatDateForAPI,
  getTransactionTimestamp,
  normalizeProviderName,
  getTransactionProvider,
  isTurboStarsTransaction,
  isBetConstructTrackerRow,
  isBetConstructTransaction,
  isBetConstructBet,
  isBetConstructResult,
  isBetConstructRollback,
  bcStateToResult,
  bcSignedDelta,
  isSportsTransaction,
  getSportsGroupingId,
  summarizeRoundTransactions
} from '../utils/betTransactions';

// Helper to get all players under a node (including the node itself if it's a player)
const getAllPlayersUnderNode = (node) => {
  const players = [];
  
  const traverse = (currentNode) => {
    if (currentNode.type === 'player') {
      players.push(currentNode);  // Return the full node object
    }
    if (currentNode.children) {
      currentNode.children.forEach(child => traverse(child));
    }
  };
  
  traverse(node);
  return players;
};

const DateRangePicker = ({ value, onChange, customStartDate, customEndDate, onCustomStartChange, onCustomEndChange }) => {
  const { t } = useTranslation();
  const [showCustom, setShowCustom] = useState(value === 'custom');
  
  useEffect(() => {
    setShowCustom(value === 'custom');
  }, [value]);
  
  const options = [
    { value: 'today', label: t('dateRange.today') },
    { value: 'last7', label: t('dateRange.last7Days') },
    { value: 'last30', label: t('dateRange.last30Days') },
    { value: 'thisMonth', label: t('dateRange.thisMonth') },
    { value: 'lastMonth', label: t('dateRange.lastMonth') },
    { value: 'custom', label: t('dateRange.custom', 'Custom Range') },
    { value: 'all', label: t('dateRange.allTime', 'All Time') }
  ];

  // Format date for datetime-local input (converts to local time for display)
  const formatDateTimeLocal = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Get timezone info for display
  const getTimezoneInfo = () => {
    const offset = new Date().getTimezoneOffset();
    const hours = Math.floor(Math.abs(offset) / 60);
    const minutes = Math.abs(offset) % 60;
    const sign = offset <= 0 ? '+' : '-';
    return `UTC${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  return (
    <div className="space-y-3">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2.5 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400 transition-colors duration-150"
      >
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      
      {showCustom && (
        <>
          <div className="text-xs text-gray-500 dark:text-gray-400 px-1">
            {t('dateRange.timezone', 'Your timezone')}: {getTimezoneInfo()}
          </div>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                {t('dateRange.startDateTime', 'Start Date & Time')}
              </label>
              <input
                type="datetime-local"
                value={formatDateTimeLocal(customStartDate)}
                onChange={(e) => {
                  const newDate = new Date(e.target.value);
                  onCustomStartChange(newDate);
                }}
                max={formatDateTimeLocal(customEndDate)}
                className="w-full px-3 py-2.5 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400"
                style={{ colorScheme: 'dark' }}
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                {t('dateRange.endDateTime', 'End Date & Time')}
              </label>
              <input
                type="datetime-local"
                value={formatDateTimeLocal(customEndDate)}
                onChange={(e) => {
                  const newDate = new Date(e.target.value);
                  onCustomEndChange(newDate);
                }}
                min={formatDateTimeLocal(customStartDate)}
                className="w-full px-3 py-2.5 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400"
                style={{ colorScheme: 'dark' }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const UserSelector = ({ selectedUsers, onUsersChange, userTree }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [availableCurrencies, setAvailableCurrencies] = useState(new Set());
  const [easterEggActive, setEasterEggActive] = useState(false);
  const [easterEggEnabled, setEasterEggEnabled] = useState(false);

  // Check if easter egg should be enabled for this user
  useEffect(() => {
    const checkEasterEggAccess = async () => {
      try {
        const selfUsername = await getSelfUsername();
        const allowedUsernames = ['agenttest', 'cityfish'];
        const isEnabled = allowedUsernames.includes(selfUsername?.toLowerCase());
        setEasterEggEnabled(isEnabled);
        
        if (isEnabled) {
          console.log('🥚 Easter egg enabled for user:', selfUsername);
        }
      } catch (error) {
        console.error('Error checking easter egg access:', error);
        setEasterEggEnabled(false);
      }
    };
    
    checkEasterEggAccess();
  }, []);

  // Easter egg: Detect ?username? pattern (only for allowed users)
  useEffect(() => {
    if (!easterEggEnabled) return;
    
    const easterEggPattern = /^\?([^?]+)\?$/;
    const match = searchQuery.match(easterEggPattern);
    
    if (match) {
      const username = match[1].trim();
      if (username) {
        // Create a fake user object for the easter egg username
        const easterEggUser = {
          username: username,
          name: username,
          type: 'player',
          attributes: {},
          balances: {},
          isEasterEgg: true
        };
        
        // Add to selected users
        onUsersChange([easterEggUser]);
        setEasterEggActive(true);
        setIsOpen(false);
        
        // Show a subtle hint
        setTimeout(() => {
          setSearchQuery('');
          setEasterEggActive(false);
        }, 2000);
      }
    }
  }, [searchQuery, onUsersChange, easterEggEnabled]);

  // Auto-expand nodes when searching
  useEffect(() => {
    if (searchQuery.length >= 2 && !searchQuery.startsWith('?')) {
      // Expand all nodes that contain matching children
      const newExpanded = new Set();
      
      const expandMatchingNodes = (node, query) => {
        if (!node) return false;
        
        let hasMatch = false;
        
        // Check if current node matches
        if (node.name?.toLowerCase().includes(query.toLowerCase()) ||
            node.username?.toLowerCase().includes(query.toLowerCase())) {
          hasMatch = true;
        }
        
        // Check children
        if (node.children) {
          for (const child of node.children) {
            if (expandMatchingNodes(child, query)) {
              hasMatch = true;
              newExpanded.add(node.username);
            }
          }
        }
        
        return hasMatch;
      };
      
      expandMatchingNodes(userTree, searchQuery);
      setExpandedNodes(newExpanded);
    }
  }, [searchQuery, userTree]);

  // Update available currencies when users are selected
  useEffect(() => {
    const currencies = new Set();
    selectedUsers.forEach(user => {
      if (user.attributes?.currencies) {
        user.attributes.currencies.forEach(curr => currencies.add(curr));
      }
    });
    setAvailableCurrencies(currencies);
  }, [selectedUsers]);

  const getUserIcon = (type) => {
    switch (type) {
      case 'admin': return BuildingOfficeIcon;
      case 'country': return GlobeAsiaAustraliaIcon;
      case 'city': return BuildingStorefrontIcon;
      case 'super-agent': return StarIcon;
      case 'agent': return UserGroupIcon;
      default: return UserIcon;
    }
  };

  const getUserStyle = (type) => {
    switch (type) {
      case 'admin': return 'text-purple-500';
      case 'country': return 'text-blue-500';
      case 'city': return 'text-green-500';
      case 'super-agent': return 'text-orange-500';
      case 'agent': return 'text-yellow-500';
      default: return 'text-gray-400';
    }
  };

  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const toggleUser = (user) => {
    // Don't allow selecting Admin, Country, or City user types
    const nonSelectableTypes = ['admin', 'country', 'city'];
    if (nonSelectableTypes.includes(user.type?.toLowerCase())) {
      return; // Exit early, don't allow selection
    }
    
    const index = selectedUsers.findIndex(u => u.username === user.username);
    let newSelected;
    
    if (index > -1) {
      // If user is already selected, deselect them
      newSelected = [];
    } else {
      // If user is not selected, select only this user (deselect others)
      newSelected = [{
        ...user,
        attributes: user.attributes || {},
        balances: user.balances || {}
      }];
    }
    
    onUsersChange(newSelected);
    
    // Auto-close the popup when a user is selected
    setIsOpen(false);
  };

  const filterUsers = (node, query) => {
    if (!node) return false;
    const matches = node.name?.toLowerCase().includes(query.toLowerCase()) ||
                   node.username?.toLowerCase().includes(query.toLowerCase());
    
    let childMatches = false;
    if (node.children) {
      childMatches = node.children.some(child => filterUsers(child, query));
    }
    
    return matches || childMatches;
  };

  // Helper function to collect all matching nodes when searching
  const collectSearchResults = (node, query) => {
    const results = [];
    const queryLower = query.toLowerCase();
    
    const traverse = (currentNode) => {
      if (!currentNode) return;
      
      // Check if current node matches
      if (currentNode.name?.toLowerCase().includes(queryLower) ||
          currentNode.username?.toLowerCase().includes(queryLower)) {
        results.push(currentNode);
      }
      
      // Check children
      if (currentNode.children) {
        currentNode.children.forEach(child => traverse(child));
      }
    };
    
    traverse(node);
    
    // Sort by username length (shortest first)
    results.sort((a, b) => {
      const usernameA = a.username || '';
      const usernameB = b.username || '';
      return usernameA.length - usernameB.length;
    });
    
    return results;
  };

  const renderUserNode = (node, level = 0, isSearchMode = false) => {
    if (!node) return null;
    
    // Apply search filter only if not in search mode (to avoid double filtering)
    if (!isSearchMode && searchQuery.length >= 2 && !filterUsers(node, searchQuery)) {
      return null;
    }

    const isExpanded = expandedNodes.has(node.username);
    const isSelected = selectedUsers.some(u => u.username === node.username);
    const hasChildren = node.children && node.children.length > 0;
    const Icon = getUserIcon(node.type);
    
    // Check if this user type is selectable
    const nonSelectableTypes = ['admin', 'country', 'city'];
    const isSelectable = !nonSelectableTypes.includes(node.type?.toLowerCase());

    // Get user currencies
    const userCurrencies = node.attributes?.currencies || [];
    const currencyDisplay = userCurrencies.length > 0 
      ? userCurrencies.join(', ') 
      : t('betHistory.noCurrencies', 'No currencies');

    return (
      <div key={node.username} className="select-none">
        <div 
          className={`flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors ${
            isSelected 
              ? 'bg-primary-100 dark:bg-primary-900/30 border border-primary-300 dark:border-primary-700' 
              : isSelectable
              ? 'hover:bg-gray-100 dark:hover:bg-gray-700'
              : 'opacity-60 cursor-not-allowed'
          }`}
          style={{ paddingLeft: `${level * 20 + 8}px` }}
          title={!isSelectable ? t('betHistory.cannotSelectUserType', 'This user type cannot be selected') : ''}
        >
          {hasChildren && !isSearchMode && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleNode(node.username);
              }}
              className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
            >
              {isExpanded ? (
                <ChevronDownIcon className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronRightIcon className="w-4 h-4 text-gray-400" />
              )}
            </button>
          )}
          {(!hasChildren || isSearchMode) && <div className="w-5" />}
          
          <div
            onClick={() => toggleUser(node)}
            className={`flex items-center gap-2 flex-1 ${
              isSelectable ? 'cursor-pointer' : 'cursor-not-allowed'
            }`}
          >
            <Icon className={`w-4 h-4 ${getUserStyle(node.type)}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className={`text-sm font-medium truncate ${
                  isSelectable 
                    ? 'text-gray-900 dark:text-gray-100' 
                    : 'text-gray-500 dark:text-gray-500'
                }`}>
                  {node.name}
                </span>
                {isSelected && (
                  <span className="text-xs px-1.5 py-0.5 bg-primary-500 text-white rounded-full">
                    {t('betHistory.selected', 'Selected')}
                  </span>
                )}
                {!isSelectable && (
                  <span className="text-xs px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-full">
                    {t('betHistory.notSelectable', 'Not selectable')}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <span>@{node.username}</span>
                <span>•</span>
                <span>{currencyDisplay}</span>
              </div>
            </div>
          </div>
        </div>
        
        {hasChildren && isExpanded && !isSearchMode && (
          <div>
            {node.children.map(child => renderUserNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  // Get unique currencies from selected users
  const selectedCurrencies = Array.from(availableCurrencies);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-white dark:bg-gray-800 border text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors w-full ${
          selectedUsers.length > 0 && selectedUsers[0].isEasterEgg
            ? 'border-purple-400 dark:border-purple-600 ring-2 ring-purple-200 dark:ring-purple-900/30'
            : 'border-gray-300 dark:border-gray-600'
        }`}
      >
        <UserIcon className={`w-4 h-4 ${selectedUsers.length > 0 && selectedUsers[0].isEasterEgg ? 'text-purple-500' : ''}`} />
        <span className="flex-1 text-left">
          {selectedUsers.length === 0 
            ? t('reports.selectUsers')
            : (
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span>{selectedUsers[0].name || selectedUsers[0].username}</span>
                  {selectedUsers[0].isEasterEgg && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-purple-500 text-white rounded-full animate-pulse">
                      🥚 Direct
                    </span>
                  )}
                </div>
                {selectedCurrencies.length > 0 && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {t('betHistory.currencies', 'Currencies')}: {selectedCurrencies.join(', ')}
                  </span>
                )}
              </div>
            )}
        </span>
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute top-full mt-2 left-0 right-0 z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 max-h-[400px] overflow-hidden flex flex-col">
            <div className="p-3 border-b border-gray-200 dark:border-gray-700">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={easterEggActive ? '🥚 Direct query activated!' : t('betHistory.searchPlaceholder', 'Search users (min 2 characters)...')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`w-full pl-9 pr-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 transition-all ${
                    easterEggActive
                      ? 'border-purple-400 dark:border-purple-600 focus:ring-purple-500 focus:border-purple-500'
                      : 'border-gray-300 dark:border-gray-600 focus:ring-primary-500 focus:border-primary-500'
                  }`}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {selectedUsers.length > 0 ? (
                    t('betHistory.userSelected', '1 user selected')
                  ) : easterEggEnabled ? (
                    <span className="text-[10px] opacity-50" title="Try ?username? for direct query">
                      💡 Tip: ?username?
                    </span>
                  ) : null}
                </span>
                {selectedUsers.length > 0 && (
                  <button
                    onClick={() => onUsersChange([])}
                    className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    {t('betHistory.clearSelection', 'Clear selection')}
                  </button>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {easterEggEnabled && searchQuery.startsWith('?') && searchQuery.length > 1 ? (
                // Easter egg hint (only for allowed users)
                <div className="text-center py-8 px-4">
                  <div className="text-4xl mb-3">🥚</div>
                  <div className="text-sm font-medium text-purple-600 dark:text-purple-400 mb-2">
                    {t('betHistory.easterEggHint', 'Direct Username Query')}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {searchQuery.match(/^\?([^?]+)\?$/) 
                      ? t('betHistory.easterEggActivated', '✨ Press Enter or wait...')
                      : t('betHistory.easterEggPattern', 'Format: ?username?')
                    }
                  </div>
                  <div className="mt-4 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                    <div className="text-xs text-purple-700 dark:text-purple-300">
                      {t('betHistory.easterEggExample', 'Example: ?player123?')}
                    </div>
                  </div>
                </div>
              ) : searchQuery.length >= 2 ? (
                // Show sorted search results
                collectSearchResults(userTree, searchQuery).map(node => 
                  renderUserNode(node, 0, true)
                )
              ) : userTree ? (
                // Show full tree when not searching
                renderUserNode(userTree)
              ) : (
                <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
                  {searchQuery.length < 2 && searchQuery.length > 0 
                    ? t('betHistory.typeMoreChars', 'Type at least 2 characters to search')
                    : t('betHistory.loadingUsers', 'Loading users...')}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};



// Humanize BetConstruct selection codes (W1, W2, X1, X2, 12, Draw, etc.)
const humanizeBetConstructSelection = (selection, home, away) => {
  if (!selection) return '';
  const code = String(selection).trim();
  const homeTeam = home || 'Home';
  const awayTeam = away || 'Away';
  const map = {
    'W1': `${homeTeam} to Win`,
    'W2': `${awayTeam} to Win`,
    'X': 'Draw',
    'Draw': 'Draw',
    '1X': `${homeTeam} or Draw`,
    'X2': `Draw or ${awayTeam}`,
    '12': `${homeTeam} or ${awayTeam}`,
  };
  return map[code] || code;
};

// Strip upstream-provider brand names from user-visible strings.
// We hide "BetConstruct" behind the generic "Sport" label.
const sanitizeProviderBrand = (str) => {
  if (!str || typeof str !== 'string') return str;
  return str
    .replace(/\bBetConstruct\s+Sportsbook\b/gi, 'Sportsbook')
    .replace(/\bBetConstruct\b/gi, 'Sport');
};

// Humanize BetConstruct bet type numeric codes
const betConstructBetTypeLabel = (code) => {
  const n = Number(code);
  if (n === 1) return 'Single';
  if (n === 2) return 'Express';
  if (n === 3) return 'System';
  if (n === 4) return 'Chain';
  return 'Single';
};

// Betinovio (Pragmatic via pm8) emits the bet debit and its settlement credit
// with DIFFERENT context.roundId values (the bet uses a short wager id, the
// settlement a long internal id), but both rows share the same
// additionalContext.wagerId. Group betinovio rounds by that wagerId so the bet
// and its win collapse into a single card like every other casino provider.
const getBetinovioRoundKey = (tx) => {
  const ac = tx?.context?.additionalContext;
  if (!ac || ac.provider !== 'betinovio') return null;
  const wagerId = ac.wagerId;
  if (wagerId === undefined || wagerId === null || wagerId === '') return null;
  return `betinovio-wager-${wagerId}`;
};

// Group transactions into rounds
const groupTransactionsByRound = (transactions) => {
  const rounds = new Map();
  const standaloneTransactions = [];
  
  // First pass: group by roundId (casino) or betId/correlationId/transactionId (sports)
  transactions.forEach(tx => {
    // Ignore internal BetConstruct tracker rows — they are scratch state,
    // not user-facing transactions.
    if (isBetConstructTrackerRow(tx)) {
      return;
    }
    // For sports bets, use betId or correlationId for grouping
    const isSportsBet = isSportsTransaction(tx);
    
    let groupingId = null;
    
    if (isSportsBet) {
      groupingId = getSportsGroupingId(tx);
    } else {
      // For casino games, use roundId — except betinovio, whose bet and
      // settlement carry different roundIds but a shared wagerId.
      groupingId = getBetinovioRoundKey(tx) || tx.context?.roundId;
    }
    
    if (groupingId) {
      if (!rounds.has(groupingId)) {
        rounds.set(groupingId, []);
      }
      rounds.get(groupingId).push(tx);
    } else {
      // Transactions without a grouping ID are treated as individual rounds
      standaloneTransactions.push([tx]);
    }
  });
  
  // Convert to array and sort each round's transactions by timestamp
  const allRounds = [
    ...Array.from(rounds.values()),
    ...standaloneTransactions
  ];
  
  return allRounds.map(roundTransactions => {
    // Sort transactions within the round by timestamp
    const sortedTransactions = roundTransactions.sort((a, b) => {
      const timestampA = getTransactionTimestamp(a);
      const timestampB = getTransactionTimestamp(b);
      return timestampA - timestampB;
    });
    
    // Get the grouping ID (could be roundId, betId, correlationId, or transactionId)
    const firstTx = sortedTransactions[0];
    const isSportsBet = isSportsTransaction(firstTx);
    
    let groupingId;
    if (isSportsBet) {
      groupingId = getSportsGroupingId(firstTx);
    } else {
      groupingId = getBetinovioRoundKey(firstTx) || firstTx.context?.roundId;
    }
    
    // Pre-calculate round statistics to avoid redundant calculations
    const {
      isTurboStarsRound,
      isBetConstructRound,
      bets,
      totalBet,
      totalWin,
      totalSettle,
      totalResettle,
      totalCancelSettle,
      netResult,
      hasRollback,
      hasSettlement,
      settleResult
    } = summarizeRoundTransactions(sortedTransactions);
    
    // Get provider and game info
    const provider = getTransactionProvider(firstTx);
    const gameCode = firstTx.context?.gameCode || firstTx.metadata?.gameCode;
    const gameDetails = firstTx.metadata?.gameDetails;
    const gameId = firstTx.context?.gameId;
    const rawGameName = gameCode || gameDetails?.name || gameId;
    
    // Resolve game name for search
    const resolvedGameName = rawGameName 
      ? (getGameNameWithFallback(rawGameName, null) || rawGameName)
      : '';
    
    // Get description for search
    const description = sanitizeProviderBrand(
      firstTx.metadata?.description || firstTx.description || ''
    );
    
    return {
      transactions: sortedTransactions,
      // Strip the internal betinovio grouping prefix so the card shows the
      // clean wager id rather than "betinovio-wager-…".
      roundId: groupingId ? String(groupingId).replace(/^betinovio-wager-/, '') : null,
      latestTimestamp: Math.max(...sortedTransactions.map(tx => getTransactionTimestamp(tx))),
      // Pre-calculated statistics
      stats: {
        isSportsBet,
        isTurboStarsRound,
        isBetConstructRound,
        totalBet,
        totalWin,
        totalSettle,
        totalResettle,
        totalCancelSettle,
        netResult,
        hasRollback,
        hasBet: bets.length > 0,
        hasSettlement,
        settleResult,
        currency: firstTx.currency,
        provider: normalizeProviderName(provider),
        rawGameName,
        resolvedGameName,
        description
      }
    };
  });
};

const RoundCard = ({ round, isFromOpenBetsTab = false, userTree, isFocused = false, canSetLimits = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(isFocused);
  const [copiedId, setCopiedId] = useState(null);
  const [limitBet, setLimitBet] = useState(null);
  
  const { transactions, roundId } = round;
  const primaryTransaction = transactions[transactions.length - 1]; // Use last transaction as primary (most recent)
  
  // Copy ID to clipboard
  const copyToClipboard = useCallback(async (text, idType) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(idType);
      setTimeout(() => setCopiedId(null), 2000); // Reset after 2 seconds
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, []);
  
  // Helper function to find user in tree
  const findUserInTree = useCallback((tree, username) => {
    if (!tree || !username) return null;
    
    // Remove currency suffix if present
    const baseUsername = username.split('-')[0];
    
    const searchTree = (node) => {
      if (!node) return null;
      
      // Check if current node matches (also remove currency from node username for comparison)
      const nodeBaseUsername = node.username?.split('-')[0];
      if (nodeBaseUsername === baseUsername) {
        return node;
      }
      
      // Search children
      if (node.children) {
        for (const child of node.children) {
          const found = searchTree(child);
          if (found) return found;
        }
      }
      
      return null;
    };
    
    return searchTree(tree);
  }, []);
  
  // Get user info from tree
  const userInfo = useMemo(() => {
    if (!primaryTransaction.playerId || !userTree) return null;
    return findUserInTree(userTree, primaryTransaction.playerId);
  }, [primaryTransaction.playerId, userTree, findUserInTree]);
  
  // Clean username (remove currency suffix)
  const cleanUsername = primaryTransaction.playerId ? primaryTransaction.playerId.split('-')[0] : null;
  
  // Calculate round statistics
  const roundStats = useMemo(() => {
    // Check if this is a TurboStars round
    const isTurboStars = transactions.some(tx => isTurboStarsTransaction(tx));
    const isBetConstruct = transactions.some(tx => isBetConstructTransaction(tx));
    
    // For TurboStars: bet is withdrawal (not containing -settle- or -rollback-)
    // For BetConstruct: use dedicated classifier
    // For other providers: bet or withdrawal with reason "bet"
    const bets = transactions.filter(tx => {
      if (isTurboStars) {
        return tx.type?.toLowerCase() === 'withdrawal' && 
               !tx.transactionId?.includes('-settle-') &&
               !tx.transactionId?.includes('-rollback-');
      }
      if (isBetConstruct) {
        return isBetConstructBet(tx);
      }
      return tx.type?.toLowerCase() === 'bet' || 
             (tx.type?.toLowerCase() === 'withdrawal' && tx.context?.reason?.toLowerCase() === 'bet');
    });
    
    const wins = transactions.filter(tx => 
      tx.type?.toLowerCase() === 'win' || 
      (tx.type?.toLowerCase() === 'deposit' && tx.context?.reason?.toLowerCase() === 'win')
    );
    
    // For TurboStars: settles are deposits with -settle- in transactionId or reason includes "settlement"/"settle"
    // For BetConstruct: deposits with reason "BetConstruct result" or bc-result-* id
    // For other providers: deposits with reason "settle"
    const settles = transactions.filter(tx => {
      if (isTurboStars) {
        return tx.type?.toLowerCase() === 'deposit' && 
               (tx.transactionId?.includes('-settle-') || 
                tx.context?.reason?.toLowerCase().includes('settlement') ||
                tx.context?.reason?.toLowerCase().includes('settle -'));
      }
      if (isBetConstruct) {
        return isBetConstructResult(tx);
      }
      return tx.type?.toLowerCase() === 'deposit' && tx.context?.reason?.toLowerCase() === 'settle';
    });
    
    // Handle rollback transactions (TurboStars specific)
    const rollbacks = transactions.filter(tx => 
      tx.type?.toLowerCase() === 'rollback' ||
      tx.transactionId?.includes('-rollback-')
    );
    
    // Handle special sports bet transactions (non-TurboStars)
    const resettles = transactions.filter(tx => 
      (tx.type?.toLowerCase() === 'deposit' || tx.type?.toLowerCase() === 'withdrawal') && 
      tx.context?.reason?.toLowerCase() === 'resettle'
    );
    const cancelSettles = transactions.filter(tx => 
      tx.type?.toLowerCase() === 'withdrawal' && tx.context?.reason?.toLowerCase() === 'cancelsettle'
    );
    
    // Check if any bet was rolled back
    const hasRollback = rollbacks.length > 0 || 
                        bets.some(b => b.rolledBack === 'true' || b.rolledBack === true);
    
    const totalBet = hasRollback ? 0 : bets.reduce((sum, tx) => sum + parseFloat(tx.amount || 0), 0);
    const totalWin = wins.reduce((sum, tx) => sum + parseFloat(tx.amount || 0), 0);
    // BetConstruct deltas are signed (resettlement/void after win can debit).
    const totalSettle = settles.reduce((sum, tx) => {
      if (isBetConstruct) return sum + bcSignedDelta(tx);
      return sum + parseFloat(tx.amount || 0);
    }, 0);
    
    // Calculate adjustments from resettles and cancellations
    const totalResettle = resettles.reduce((sum, tx) => {
      // Resettles can be either deposits (adding money) or withdrawals (removing money)
      const amount = parseFloat(tx.amount || 0);
      return sum + (tx.type?.toLowerCase() === 'deposit' ? amount : -amount);
    }, 0);
    const totalCancelSettle = cancelSettles.reduce((sum, tx) => sum - parseFloat(tx.amount || 0), 0);
    
    // Net result includes all settlements, resettlements, and cancellations
    // For rollbacks, net result is 0 (bet was refunded)
    const netResult = hasRollback ? 0 : totalWin + totalSettle + totalResettle + totalCancelSettle - totalBet;
    const multiplier = totalBet > 0 ? ((totalWin + totalSettle) / totalBet) : 0;
    
    // Determine platform
    const platform = primaryTransaction.platform?.toLowerCase();
    const product = primaryTransaction.context?.product?.toLowerCase();
    const isPlatformSport = platform === 'sport' || platform === 'sports' || product === 'sportsbook';
    const isCasino = platform === 'casino' || platform === 'pm8' || (!isPlatformSport && platform);
    
    // For TurboStars: check resultType on bet or settled field
    const turboStarsBetTx = isTurboStars ? bets[0] : null;
    const turboStarsSettled = turboStarsBetTx?.settled === 'true' || turboStarsBetTx?.settled === true;
    const turboStarsResultType = turboStarsBetTx?.resultType || 
                                  settles[0]?.context?.additionalContext?.resultType;
    
    // For BetConstruct: determine result from authoritative bcBetState
    // (see Spring Partner API BetState enum).
    let betConstructResult = null;
    if (isBetConstruct && settles.length > 0) {
      const latestStateRow = [...settles].sort(
        (a, b) => getTransactionTimestamp(b) - getTransactionTimestamp(a)
      )[0];
      const latestState = Number(latestStateRow?.context?.additionalContext?.bcBetState);
      betConstructResult = bcStateToResult(latestState);
    }
    
    // Determine if bet is in progress (no settlement or win yet)
    const hasSettlement = settles.length > 0 || resettles.length > 0 || cancelSettles.length > 0 || 
                          (isTurboStars && turboStarsSettled);
    const isInProgress = bets.length > 0 && !hasSettlement && !hasRollback && wins.length === 0 && !isFromOpenBetsTab;
    
    // Determine round status
    let status = 'unknown';
    let statusLabel = '';
    
    if (hasRollback) {
      status = 'cancelled';
      statusLabel = t('betHistory.cancelled', 'Cancelled');
    } else if (isFromOpenBetsTab) {
      status = 'pending';
      statusLabel = t('betHistory.pending', 'Pending');
    } else if (hasSettlement || wins.length > 0) {
      // Check if this has been adjusted after initial settlement
      const hasAdjustments = resettles.length > 0 || cancelSettles.length > 0;
      
      // For TurboStars: check resultType
      // For other sports: check the latest settle/resettle transaction for win/loss
      let settleResult = null;
      
      if (isTurboStars && turboStarsResultType) {
        if (turboStarsResultType === 'won') settleResult = 'Win';
        else if (turboStarsResultType === 'lost') settleResult = 'Lose';
        else if (turboStarsResultType === 'cashout') settleResult = 'Cashout';
      } else if (isBetConstruct && betConstructResult) {
        settleResult = betConstructResult;
      } else {
        const allSettleTxs = [...settles, ...resettles].sort((a, b) => 
          getTransactionTimestamp(b) - getTransactionTimestamp(a)
        );
        const latestSettle = allSettleTxs[0];
        settleResult = latestSettle?.context?.extras?.bet?.result || latestSettle?.context?.bet?.result;
      }
      
      if (settleResult === 'Cashout') {
        status = 'cashout';
        statusLabel = t('betHistory.cashout', 'Cashed Out');
      } else if (settleResult === 'Void') {
        // BetConstruct BetState = 2 (Returned). Stake refunded; treat as void.
        status = 'cancelled';
        statusLabel = t('betHistory.void', 'Void');
      } else if (settleResult === 'Pending') {
        // BetConstruct BetState = 1 (Accepted / revert-to-unresulted).
        status = 'inProgress';
        statusLabel = t('betHistory.inProgress', 'In Progress');
      } else if (settleResult === 'Win' || netResult > 0) {
        status = 'won';
        statusLabel = hasAdjustments ? t('betHistory.wonAdjusted', 'Won (Adjusted)') : t('betHistory.won', 'Won');
      } else if (settleResult === 'Lose' || netResult < 0) {
        status = 'lost';
        statusLabel = hasAdjustments ? t('betHistory.lostAdjusted', 'Lost (Adjusted)') : t('betHistory.lost', 'Lost');
      } else if (wins.length > 0 && totalWin === 0) {
        status = 'roundClosed';
        statusLabel = t('betHistory.roundClosed', 'Round Closed');
      } else if (wins.length > 0) {
        status = 'won';
        statusLabel = t('betHistory.win', 'Win');
      } else {
        status = 'settled';
        statusLabel = hasAdjustments ? t('betHistory.settledAdjusted', 'Settled (Adjusted)') : t('betHistory.settled', 'Settled');
      }
    } else if (isInProgress) {
      status = 'inProgress';
      statusLabel = t('betHistory.inProgress', 'In Progress');
    } else if (bets.length > 0) {
      status = 'bet';
      statusLabel = t('betHistory.betPlaced', 'Bet Placed');
    }
    
    // Get round duration
    const timestamps = transactions.map(tx => getTransactionTimestamp(tx));
    const roundStart = Math.min(...timestamps);
    const roundEnd = Math.max(...timestamps);
    const duration = roundEnd - roundStart;
    
    return {
      totalBet,
      totalWin,
      totalSettle,
      netResult,
      multiplier,
      duration,
      betCount: bets.length,
      winCount: wins.length,
      settleCount: settles.length,
      status,
      statusLabel,
      isPlatformSport,
      isCasino,
      currency: primaryTransaction.currency,
      isTurboStars,
      isBetConstruct,
      hasRollback
    };
  }, [transactions, isFromOpenBetsTab, t]);
  
  // Format game name using mapping utility with fallback formatting
  const formatGameName = (gameCode) => {
    if (!gameCode) return null;
    
    // First try to get the game name from the mapping
    const mappedName = getGameNameWithFallback(gameCode, null);
    if (mappedName) {
      return mappedName;
    }
    
    // Fallback: Convert IDs like "vs20olympx" to readable names
    return gameCode
      .replace(/^vs\d+/, '') // Remove "vs20" prefix
      .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space before capitals
      .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2') // Handle consecutive capitals
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };
  
  // Get detailed game info from primary transaction
  const gameInfo = useMemo(() => {
    // Try to get gameCode first, then fallback to other fields
    const gameCode = primaryTransaction.context?.gameCode || primaryTransaction.metadata?.gameCode;
    const gameDetails = primaryTransaction.metadata?.gameDetails;
    const gameId = primaryTransaction.context?.gameId;
    const rawGameName = gameCode || gameDetails?.name || gameId;
    const provider = primaryTransaction.context?.additionalContext?.provider || 
                    primaryTransaction.context?.provider || 
                    primaryTransaction.metadata?.provider;
    const sanitizedRawName = sanitizeProviderBrand(rawGameName);
    
    return {
      name: formatGameName(sanitizedRawName), // Now uses the mapping utility
      rawName: sanitizedRawName,
      gameCode: gameCode, // Store the original gameCode for reference
      category: gameDetails?.category || primaryTransaction.context?.product,
      provider: normalizeProviderName(provider),
      description: sanitizeProviderBrand(primaryTransaction.metadata?.description || primaryTransaction.description)
    };
  }, [primaryTransaction]);
  
  // Extract sports betting details
  const sportsBetInfo = useMemo(() => {
    if (!roundStats.isPlatformSport) return null;
    
    // Check if this is a TurboStars bet
    const isTurboStars = transactions.some(tx => isTurboStarsTransaction(tx));
    const isBetConstruct = transactions.some(tx => isBetConstructTransaction(tx));
    
    if (isBetConstruct) {
      // BetConstruct format handling
      const betTransaction = transactions.find(tx => isBetConstructBet(tx));
      const resultTransactions = transactions
        .filter(tx => isBetConstructResult(tx))
        .sort((a, b) => getTransactionTimestamp(a) - getTransactionTimestamp(b));
      const rollbackTransaction = transactions.find(tx => isBetConstructRollback(tx));
      
      if (!betTransaction) return null;
      
      const additionalContext = betTransaction.context?.additionalContext || {};
      const selectionsRaw = additionalContext.selections || [];
      
      const betTypeCode = additionalContext.betType;
      const betTypeLabel = betConstructBetTypeLabel(betTypeCode);
      // Normalize to "Express" (multi) vs "Single" for downstream display,
      // but expose the precise label (Single/Express/System/Chain) too.
      const betType = (betTypeLabel === 'Single') ? 'Single' : 'Express';
      const betOdds = parseFloat(additionalContext.totalPrice || 1);
      const isFreebet = additionalContext.isFreebet === true ||
                        additionalContext.isFreebet === 'true';
      
      // Aggregate signed delta (authoritative, signed)
      const totalPayout = resultTransactions.reduce(
        (sum, tx) => sum + bcSignedDelta(tx),
        0
      );
      const totalStake = parseFloat(betTransaction.amount || 0);
      // Latest state wins if multiple (resettlements overwrite each other)
      const latestResultTx = resultTransactions[resultTransactions.length - 1];
      const latestState = Number(latestResultTx?.context?.additionalContext?.bcBetState);
      const latestCtx = latestResultTx?.context?.additionalContext || {};
      const winningTax = parseFloat(latestCtx.winningTax || 0);
      const isVoid = latestCtx.isVoid === true || latestCtx.isVoid === 'true' || latestState === 2;
      const isResettled = resultTransactions.length > 1 ||
                          latestCtx.isResettled === true ||
                          latestCtx.isResettled === 'true';
      const isPartial = latestCtx.isPartial === true || latestCtx.isPartial === 'true';
      
      let betResult = 'Unknown';
      const isRolled = !!rollbackTransaction ||
                       betTransaction.rolledBack === 'true' ||
                       betTransaction.rolledBack === true;
      if (isRolled) {
        betResult = 'Cancelled';
      } else if (resultTransactions.length > 0) {
        const mapped = bcStateToResult(latestState);
        if (mapped === 'Pending') {
          // Revert-to-unresulted — treat as pending
          betResult = 'Unknown';
        } else if (mapped === 'Void') {
          betResult = 'Void';
        } else if (mapped) {
          betResult = mapped;
        }
      }
      
      // Build per-selection items. For Single/Express we know collective result;
      // per-selection result is inferred from collective result.
      const items = selectionsRaw.map((sel, idx) => {
        const home = sel.HomeTeamName;
        const away = sel.AwayTeamName;
        const competitors = [home, away].filter(Boolean);
        const isLive = sel.IsLive === true || sel.IsLive === 'true';
        const startTime = sel.MatchStartDate ? new Date(sel.MatchStartDate) : null;
        const sportName = sel.SportFullName || sel.SportName || 'Sport';
        const tournament = sel.CompetitionName || 'Tournament';
        const region = sel.RegionName || null;
        const price = parseFloat(sel.Price ?? 1);
        const rawSelection = sel.SelectionName;
        const humanSelection = humanizeBetConstructSelection(rawSelection, home, away);
        const market = sel.MarketName || 'Market';
        
        // Per-selection result: for Single we can use the overall result directly.
        // For Express: if overall Win, every selection won; if Lose, at least one lost
        //   (we can't identify which) so mark unknown selections as Pending.
        // For System/Chain: unknown → Pending.
        let selResult = 'Unknown';
        if (isRolled) {
          selResult = 'Cancelled';
        } else if (betResult === 'Void') {
          selResult = 'Void';
        } else if (betType === 'Single') {
          if (['Win', 'Lose', 'Cashout', 'Void'].includes(betResult)) {
            selResult = betResult;
          }
        } else if (betResult === 'Win') {
          selResult = 'Win';
        } else if (betResult === 'Cashout') {
          selResult = 'Cashout';
        }
        // Else leave as 'Unknown' (pending-style display) for multi-bet losses
        
        return {
          index: idx,
          event: {
            name: sel.MatchName || competitors.join(' vs '),
            sport: sportName,
            tournament,
            category: region,
            startTime,
            stage: isLive ? 'Live' : 'Prematch',
            competitors,
          },
          selections: [{
            market,
            selection: humanSelection,
            rawSelection,
            odds: price,
            result: selResult,
          }],
          acceptedOdd: price,
          result: selResult,
        };
      });
      
      return {
        betType,
        betTypeLabel,
        betOdds,
        betResult,
        totalPayout,
        totalStake,
        items,
        isSettled: resultTransactions.length > 0,
        provider: 'Sport',
        isCashout: betResult === 'Cashout',
        isRolledBack: isRolled,
        isVoid,
        isResettled,
        isPartial,
        isFreebet,
        winningTax,
        bcBetState: isNaN(latestState) ? null : latestState,
      };
    }
    
    if (isTurboStars) {
      // TurboStars format handling
      // Find the bet transaction (withdrawal)
      const betTransaction = transactions.find(tx => 
        tx.type?.toLowerCase() === 'withdrawal' && 
        !tx.transactionId?.includes('-settle-') &&
        !tx.transactionId?.includes('-rollback-')
      );
      
      // Find settle transaction (deposit with -settle- in ID or reason includes "settlement")
      const settleTransaction = transactions.find(tx => 
        tx.type?.toLowerCase() === 'deposit' && 
        (tx.transactionId?.includes('-settle-') || 
         tx.context?.reason?.toLowerCase().includes('settlement') ||
         tx.context?.reason?.toLowerCase().includes('settle'))
      );
      
      // Find rollback transaction
      const rollbackTransaction = transactions.find(tx => 
        tx.type?.toLowerCase() === 'rollback' ||
        tx.transactionId?.includes('-rollback-')
      );
      
      if (!betTransaction) return null;
      
      const additionalContext = betTransaction.context?.additionalContext || {};
      const betMetadata = additionalContext.betMetadata || [];
      
      // Parse bet type from ticketType
      const ticketType = additionalContext.ticketType || 'single';
      const betType = ticketType === 'express' ? 'Express' : 'Single';
      const betOdds = parseFloat(additionalContext.coefficient || 1);
      
      // Determine bet result
      let betResult = 'Unknown';
      if (rollbackTransaction || betTransaction.rolledBack === 'true' || betTransaction.rolledBack === true) {
        betResult = 'Cancelled';
      } else if (settleTransaction) {
        const settleResultType = settleTransaction.context?.additionalContext?.resultType;
        if (settleResultType === 'won') betResult = 'Win';
        else if (settleResultType === 'lost') betResult = 'Lose';
        else if (settleResultType === 'cashout') betResult = 'Cashout';
        else betResult = settleResultType || 'Unknown';
      } else if (betTransaction.resultType) {
        // Result might be on the bet transaction itself
        const resultType = betTransaction.resultType;
        if (resultType === 'won') betResult = 'Win';
        else if (resultType === 'lost') betResult = 'Lose';
        else if (resultType === 'cashout') betResult = 'Cashout';
        else betResult = resultType;
      }
      
      // Parse bet items from betMetadata
      const items = betMetadata.map((meta, idx) => {
        // Extract event details from TurboStars format
        const eventName = meta.event || 'Unknown Event';
        const competitors = eventName.includes(' vs ') ? eventName.split(' vs ') : [eventName];
        
        const eventInfo = {
          name: eventName,
          sport: meta.discipline || meta.disciplineId || 'Unknown',
          tournament: meta.tournament || 'Unknown',
          category: meta.tournamentRegionCode || null,
          startTime: meta.eventDate ? new Date(meta.eventDate) : null,
          stage: meta.stage === 1 ? 'Prematch' : meta.stage === 2 ? 'Live' : 'Unknown',
          competitors: competitors
        };
        
        // For TurboStars, market/outcome are directly on the metadata
        const selections = [{
          market: meta.market || 'Unknown',
          selection: meta.outcome || 'Unknown',
          odds: parseFloat(meta.coefficient || 1),
          result: betResult === 'Win' ? 'Win' : 
                  betResult === 'Lose' ? 'Lose' : 
                  betResult === 'Cashout' ? 'Cashout' :
                  betResult === 'Cancelled' ? 'Cancelled' : 'Unknown'
        }];
        
        return {
          index: idx,
          event: eventInfo,
          selections,
          acceptedOdd: parseFloat(meta.coefficient || 1),
          result: selections[0].result
        };
      });
      
      return {
        betType,
        betOdds,
        betResult,
        items,
        isSettled: !!settleTransaction || betTransaction.settled === 'true' || betTransaction.settled === true,
        provider: 'TurboStars',
        isCashout: betResult === 'Cashout',
        isRolledBack: !!rollbackTransaction || betTransaction.rolledBack === 'true' || betTransaction.rolledBack === true
      };
    }
    
    // Original provider format handling
    // Look for bet transaction (withdrawal with reason "bet")
    const betTransaction = transactions.find(tx => 
      tx.type?.toLowerCase() === 'withdrawal' && tx.context?.reason?.toLowerCase() === 'bet'
    );
    
    // Look for settle transaction (deposit with reason "settle") which has the results
    const settleTransaction = transactions.find(tx => 
      tx.type?.toLowerCase() === 'deposit' && tx.context?.reason?.toLowerCase() === 'settle'
    );
    
    // Use bet transaction for initial data, settle transaction for results (if available)
    const primaryBetTx = settleTransaction || betTransaction || primaryTransaction;
    const betExtras = primaryBetTx.context?.extras?.bet;
    if (!betExtras) return null;
    
    // Parse bet type
    const betType = betExtras.betType || 'Unknown';
    const betOdds = parseFloat(betExtras.betOdd || 1);
    const betResult = betExtras.result || 'Unknown';
    
    // Parse bet items (selections) - these will have results if it's a settle transaction
    const items = betExtras.items?.map((item, idx) => {
      const event = item.event || {};
      const outcomes = item.sourceOutcomes || [];
      
      // Extract event details
      const eventInfo = {
        name: event.eventName,
        sport: event.sportName,
        tournament: event.tournamentName,
        category: event.categoryName,
        startTime: event.startTime ? new Date(event.startTime) : null,
        stage: item.eventStage || 'Unknown',
        competitors: event.competitors?.map(c => c.competitorName).filter(Boolean) || []
      };
      
      // Extract selection details - the result will be in the outcome if settled
      const selections = outcomes.map(outcome => ({
        market: outcome.marketName,
        selection: outcome.outcomeName,
        odds: parseFloat(outcome.acceptedOdd || 1),
        result: outcome.result || item.result || 'Unknown'
      }));
      
      return {
        index: idx,
        event: eventInfo,
        selections,
        acceptedOdd: parseFloat(item.acceptedOdd || 1),
        result: item.result || outcomes[0]?.result || 'Unknown'
      };
    }) || [];
    
    return {
      betType,
      betOdds,
      betResult,
      items,
      isSettled: !!settleTransaction,
      provider: 'Default'
    };
  }, [roundStats.isPlatformSport, transactions, primaryTransaction]);

  // What an admin would be capping if they set a limit straight off this bet:
  // the league it was placed in for sports, the game itself for casino.
  const primaryLimitBet = useMemo(() => {
    // Without a player there is no chain to hang a limit on
    if (!canSetLimits || !cleanUsername) return null;

    if (sportsBetInfo && sportsBetInfo.items.length > 0) {
      const { tournament, sport } = sportsBetInfo.items[0].event;
      return { isSports: true, leagueName: tournament, sportName: sport };
    }

    const gameCode = gameInfo.gameCode || gameInfo.rawName;
    if (!gameCode) return null;

    return {
      isSports: false,
      gameCode,
      gameName: gameInfo.name,
      providerLabel: gameInfo.provider
    };
  }, [canSetLimits, cleanUsername, sportsBetInfo, gameInfo]);

  const limitButtonLabel = primaryLimitBet?.isSports
    ? primaryLimitBet.leagueName
      ? t('limitScope.limitLeague', 'Limit league')
      : t('limitScope.limitSport', 'Limit sport')
    : t('limitScope.limitGame', 'Limit game');

  // Kept stable so re-rendering the card does not reset the open limit panel
  const limitPlayer = useMemo(
    () => ({ username: cleanUsername, name: userInfo?.name, type: userInfo?.type }),
    [cleanUsername, userInfo]
  );

  // Get free spin info from primary transaction
  const freeSpinInfo = primaryTransaction.context?.additionalContext?.freeSpinData;
  const hasFreeSpins = freeSpinInfo?.freeSpin === 'yes';
  
  // Get balance breakdown from primary transaction (which is now the latest/last transaction)
  const balanceBreakdown = {
    cash: parseFloat(primaryTransaction.balanceAfter?.cash || 0),
    bonus: parseFloat(primaryTransaction.balanceAfter?.bonus || 0),
    locked: parseFloat(primaryTransaction.balanceAfter?.locked || 0)
  };
  const totalBalance = balanceBreakdown.cash + balanceBreakdown.bonus + balanceBreakdown.locked;
  
  // Get color coding for round status
  const getStatusColor = () => {
    switch (roundStats.status) {
      case 'pending':
        return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-700';
      case 'inProgress':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700';
      case 'won':
        if (roundStats.multiplier >= 10) {
          return 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-700';
        } else if (roundStats.multiplier >= 5) {
          return 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700';
        } else {
          return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-700';
        }
      case 'lost':
        return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-700';
      case 'roundClosed':
        return 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700';
      case 'cancelled':
        return 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700';
      case 'cashout':
        return 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 border-cyan-200 dark:border-cyan-700';
      case 'settled':
        return roundStats.netResult >= 0 
          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-700'
          : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-700';
      default:
        return 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700';
    }
  };
  
  const getAmountColor = () => {
    if (roundStats.netResult > 0) {
      if (roundStats.multiplier >= 10) return 'text-purple-600 dark:text-purple-400';
      if (roundStats.multiplier >= 5) return 'text-emerald-600 dark:text-emerald-400';
      return 'text-green-600 dark:text-green-400';
    } else if (roundStats.netResult < 0) {
      return 'text-red-600 dark:text-red-400';
    } else {
      return 'text-gray-600 dark:text-gray-400';
    }
  };
  // Get round type label
  const getRoundTypeLabel = () => {
    if (roundStats.isPlatformSport) {
      return t('betHistory.sportsBet', 'Sports Bet');
    } else if (roundStats.isCasino) {
      return t('betHistory.casinoBet', 'Casino Bet');
    } else {
      return t('betHistory.gameRound', 'Game Round');
    }
  };

  // Get card theme based on round status
  const getCardTheme = () => {
    const baseStyle = 'bg-white dark:bg-gray-800 border';
    switch (roundStats.status) {
      case 'pending':
        return `${baseStyle} border-amber-200 dark:border-amber-700`;
      case 'inProgress':
        return `${baseStyle} border-blue-200 dark:border-blue-700`;
      case 'won':
        if (roundStats.multiplier >= 10) {
          return `${baseStyle} border-purple-200 dark:border-purple-700`;
        } else if (roundStats.multiplier >= 5) {
          return `${baseStyle} border-emerald-200 dark:border-emerald-700`;
        } else {
          return `${baseStyle} border-green-200 dark:border-green-700`;
        }
      case 'lost':
        return `${baseStyle} border-red-200 dark:border-red-700`;
      case 'cancelled':
        return `${baseStyle} border-gray-200 dark:border-gray-700`;
      case 'cashout':
        return `${baseStyle} border-cyan-200 dark:border-cyan-700`;
      case 'settled':
        return roundStats.netResult >= 0 
          ? `${baseStyle} border-green-200 dark:border-green-700`
          : `${baseStyle} border-red-200 dark:border-red-700`;
      default:
        return `${baseStyle} border-gray-200 dark:border-gray-700`;
    }
  };

  const typeLabel = getRoundTypeLabel();
  const statusColor = getStatusColor();
  const amountColor = getAmountColor();
  const cardTheme = getCardTheme();
  
  // Get timestamp from the primary transaction (which is now the latest transaction)
  const txDate = new Date(primaryTransaction.timestampISO || primaryTransaction.timestamp || primaryTransaction.timestampNumber);
  const now = new Date();
  const isToday = format(now, 'yyyy-MM-dd') === format(txDate, 'yyyy-MM-dd');
  const isYesterday = format(new Date(now.getTime() - 24 * 60 * 60 * 1000), 'yyyy-MM-dd') === format(txDate, 'yyyy-MM-dd');
  const isThisYear = now.getFullYear() === txDate.getFullYear();
  
  // Format date based on recency
  let dateLabel;
  if (isToday) {
    dateLabel = t('betHistory.today', 'Today');
  } else if (isYesterday) {
    dateLabel = t('betHistory.yesterday', 'Yesterday');
  } else if (isThisYear) {
    dateLabel = format(txDate, 'MMM dd'); // Oct 01
  } else {
    dateLabel = format(txDate, 'MMM dd, yyyy'); // Oct 01, 2024
  }
  
  const formattedTime = format(txDate, 'HH:mm:ss');
  const fullDateTime = format(txDate, 'PPpp'); // Full format for tooltip

  // Format net result for display
  const displayAmount = roundStats.netResult;
  const formattedAmount = formatCurrency(Math.abs(displayAmount), roundStats.currency);

  return (
    <div 
      id={`round-${roundId || primaryTransaction.transactionId}`}
      className={`
        relative rounded-lg transition-all duration-200 shadow-sm
        ${cardTheme}
        ${isFocused ? 'ring-2 ring-amber-400 dark:ring-amber-500' : ''}
        hover:shadow-md cursor-pointer overflow-hidden
      `}
    >
      {/* Main Content */}
      <div className="p-3 sm:p-4" onClick={() => setIsExpanded(!isExpanded)}>
        {/* Header Row */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            {/* Status Indicator */}
            <div className="flex-shrink-0">
              <div className={`w-3 h-3 rounded-full ${statusColor.split(' ')[0]} mt-1`}></div>
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                  {typeLabel}
                </span>
                
                {/* Status Badge */}
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusColor}`}>
                  {roundStats.statusLabel}
                </span>
                
                {/* Transaction Count Badge */}
                {transactions.length > 1 && (
                  <span className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full">
                    {transactions.length} {t('betHistory.transactions', 'transactions')}
                  </span>
                )}
                
                {hasFreeSpins && (
                  <span className="px-1.5 py-0.5 text-[10px] font-medium bg-purple-200 dark:bg-purple-800 text-purple-800 dark:text-purple-200 rounded-full">
                    {t('betHistory.freeSpins', 'FREE SPINS')}
                  </span>
                )}
                
                {/* Provider Badge for Sports */}
                {roundStats.isPlatformSport && sportsBetInfo?.provider && (
                  <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-full inline-flex items-center gap-1 ${
                    sportsBetInfo.provider === 'TurboStars'
                      ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                      : sportsBetInfo.provider === 'Sport'
                      ? 'bg-gradient-to-r from-amber-100 to-orange-100 dark:from-amber-900/40 dark:to-orange-900/40 text-orange-700 dark:text-orange-300 ring-1 ring-orange-300/50 dark:ring-orange-700/50'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                  }`}>
                    {sportsBetInfo.provider === 'Sport' && (
                      <span className="w-1.5 h-1.5 rounded-full bg-orange-500 dark:bg-orange-400" />
                    )}
                    {sportsBetInfo.provider}
                  </span>
                )}
                
                {/* Cashout Badge */}
                {sportsBetInfo?.isCashout && (
                  <span className="px-1.5 py-0.5 text-[10px] font-medium bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 rounded-full">
                    {t('betHistory.cashedOut', 'Cashed Out')}
                  </span>
                )}
                
                {/* Rolled Back Badge */}
                {sportsBetInfo?.isRolledBack && (
                  <span className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full">
                    {t('betHistory.cancelled', 'Cancelled')}
                  </span>
                )}
              </div>
              
              {/* Sports Bet Info or Game Info */}
              {sportsBetInfo && sportsBetInfo.items.length > 0 ? (
                <div className="space-y-1">
                  {/* Bet Type and Odds */}
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                      sportsBetInfo.betType === 'Express' 
                        ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                        : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    }`}>
                      {sportsBetInfo.betType === 'Express' ? t('betHistory.multiBet', 'Multi') : t('betHistory.singleBet', 'Single')}
                    </span>
                    <span className="text-xs font-bold text-gray-700 dark:text-gray-300">
                      @ {sportsBetInfo.betOdds.toFixed(2)}
                    </span>
                  </div>
                  
                  {/* First Event (or only event for single bets) */}
                  <div>
                    <div className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      {sportsBetInfo.items[0].event.competitors.length > 0 
                        ? sportsBetInfo.items[0].event.competitors.join(' vs ')
                        : sportsBetInfo.items[0].event.name}
                    </div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400">
                      {sportsBetInfo.items[0].selections[0]?.market} • {sportsBetInfo.items[0].selections[0]?.selection}
                    </div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400">
                      {sportsBetInfo.items[0].event.sport} • {sportsBetInfo.items[0].event.tournament}
                    </div>
                    {sportsBetInfo.items.length > 1 && (
                      <div className="text-[10px] text-primary-600 dark:text-primary-400 mt-1">
                        +{sportsBetInfo.items.length - 1} {t('betHistory.moreSelections', 'more selections')}
                      </div>
                    )}
                  </div>
                </div>
              ) : gameInfo.name ? (
                <div>
                  <div className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                    {gameInfo.name}
                  </div>
                  <div className="text-[10px] text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                    {gameInfo.provider && (
                      <>
                        <span>{gameInfo.provider}</span>
                        <span>•</span>
                      </>
                    )}
                    <span>{primaryTransaction.platform}</span>
                  </div>
                </div>
              ) : null}
              
              {/* Player ID and Date/Time */}
              <div className="flex flex-wrap items-center gap-1.5 mt-1">
                {/* Player Button */}
                {cleanUsername ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      // Navigate to user deep view
                      navigate(`/users/${cleanUsername}/deep-view`);
                    }}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-gray-50 dark:bg-gray-800/50 hover:bg-primary-100 dark:hover:bg-primary-900/30 hover:text-primary-600 dark:hover:text-primary-400 border border-transparent hover:border-primary-300 dark:hover:border-primary-700 transition-all duration-150 group cursor-pointer z-10 relative text-[10px]"
                    title={userInfo ? `${userInfo.name} (@${cleanUsername}) - Click to view details` : `@${cleanUsername} - Click to view details`}
                  >
                    <UserIcon className="w-3 h-3 text-primary-500 dark:text-primary-400 group-hover:scale-110 transition-transform flex-shrink-0" />
                    <span className="font-semibold group-hover:underline text-gray-700 dark:text-gray-300">
                      {userInfo ? userInfo.name : cleanUsername}
                    </span>
                    {userInfo && cleanUsername && (
                      <span className="font-mono text-[9px] text-gray-400 dark:text-gray-500">
                        @{cleanUsername}
                      </span>
                    )}
                    <ChevronRightIcon className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                ) : (
                  <div className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] text-gray-500 dark:text-gray-400">
                    <UserIcon className="w-3 h-3" />
                    <span className="font-medium">
                      {primaryTransaction.playerId || 'Unknown'}
                    </span>
                  </div>
                )}
                
                {/* Date/Time Badge */}
                <div 
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 cursor-help"
                  title={fullDateTime}
                >
                  <CalendarIcon className="w-3 h-3 flex-shrink-0" />
                  <div className="flex items-center gap-1 text-[10px]">
                    <span className="font-semibold whitespace-nowrap">{dateLabel}</span>
                    <span className="text-blue-600 dark:text-blue-400">•</span>
                    <span className="font-mono whitespace-nowrap">{formattedTime}</span>
                  </div>
                </div>
                
                {/* Round ID Badge */}
                {roundId && (
                  <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 text-[10px]">
                    <span className="font-mono">
                      {roundId.length > 10 ? `${roundId.substring(0, 8)}...` : roundId}
                    </span>
                  </div>
                )}
              </div>

              {/* Set a bet limit on what this round was played on */}
              {primaryLimitBet && (
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setLimitBet(primaryLimitBet);
                    }}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/40 active:bg-amber-200 dark:active:bg-amber-900/60 transition-colors text-[11px] font-semibold touch-manipulation cursor-pointer relative z-10"
                    title={
                      primaryLimitBet.isSports
                        ? `${limitButtonLabel}: ${primaryLimitBet.leagueName || primaryLimitBet.sportName || ''}`
                        : `${limitButtonLabel}: ${primaryLimitBet.gameName || primaryLimitBet.gameCode}`
                    }
                  >
                    <ShieldExclamationIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    {limitButtonLabel}
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {/* Net Result Display */}
          <div className="text-right flex-shrink-0">
            <div 
              className={`text-lg sm:text-xl font-bold ${amountColor}`}
              title={createAmountTooltip(displayAmount, roundStats.currency, formattedAmount)}
            >
              {displayAmount > 0 ? '+' : displayAmount < 0 ? '-' : ''}
              {formattedAmount}
            </div>
            {roundStats.multiplier > 0 && roundStats.totalBet > 0 && (
              <div className="text-[10px] text-gray-500 dark:text-gray-400">
                {roundStats.multiplier.toFixed(2)}x
              </div>
            )}
            <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
              {t('betHistory.netResult', 'Net')}
            </div>
          </div>
        </div>

        {/* Round Summary Row */}
        <div className="mt-3 pt-3 border-t border-gray-200/50 dark:border-gray-700/50">
          <div className="grid grid-cols-4 gap-2 text-center">
            <div>
              <div className="text-[10px] text-gray-500 dark:text-gray-400">{t('betHistory.totalBet', 'Total Bet')}</div>
              <div 
                className="text-xs font-medium text-blue-600 dark:text-blue-400"
                title={formatCurrency(roundStats.totalBet, roundStats.currency, { compact: false })}
              >
                {formatCurrency(roundStats.totalBet, roundStats.currency)}
              </div>
            </div>
            <div>
              <div className="text-[10px] text-gray-500 dark:text-gray-400">{t('betHistory.totalWin', 'Total Win')}</div>
              <div 
                className={`text-xs font-medium ${roundStats.totalWin > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-500'}`}
                title={formatCurrency(roundStats.totalWin + roundStats.totalSettle, roundStats.currency, { compact: false })}
              >
                {formatCurrency(roundStats.totalWin + roundStats.totalSettle, roundStats.currency)}
              </div>
            </div>
            <div>
              <div className="text-[10px] text-gray-500 dark:text-gray-400">{t('betHistory.balance', 'Balance')}</div>
              <div 
                className="text-xs font-medium text-gray-700 dark:text-gray-300"
                title={formatCurrency(totalBalance, roundStats.currency, { compact: false })}
              >
                {formatCurrency(totalBalance, roundStats.currency)}
              </div>
            </div>
            <div className="flex items-center justify-center">
              <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 p-2.5 sm:p-3 space-y-2.5">
          {/* All Transactions in Round - Clean Layout */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
            <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2.5 uppercase tracking-wider">
              {transactions.length === 1 
                ? t('betHistory.transactionDetails', 'Transaction Details')
                : t('betHistory.roundTransactions', 'Round Transactions')
              }
              <span className="ml-1.5 text-[10px] font-normal">({transactions.length})</span>
            </h4>
            
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {transactions.map((tx, index) => {
                const reason = tx.context?.reason?.toLowerCase() || '';
                const txType = tx.type?.toLowerCase();
                const isTurboStarsTx = isTurboStarsTransaction(tx);
                const isBetConstructTx = isBetConstructTransaction(tx);
                
                // TurboStars-specific transaction type detection
                const isTurboStarsBet = isTurboStarsTx && txType === 'withdrawal' && 
                                        !tx.transactionId?.includes('-settle-') && 
                                        !tx.transactionId?.includes('-rollback-');
                const isTurboStarsSettle = isTurboStarsTx && txType === 'deposit' && 
                                           (tx.transactionId?.includes('-settle-') || 
                                            reason.includes('settlement') || reason.includes('settle'));
                const isTurboStarsRollback = isTurboStarsTx && 
                                              (txType === 'rollback' || tx.transactionId?.includes('-rollback-'));
                
                // BetConstruct-specific transaction type detection
                const isBCBetTx = isBetConstructTx && isBetConstructBet(tx);
                const isBCResultTx = isBetConstructTx && isBetConstructResult(tx);
                const isBCRollback = isBetConstructTx &&
                                     (txType === 'rollback' || tx.transactionId?.includes('-rollback-'));
                
                // Generic transaction type detection
                const isBet = txType === 'bet' || 
                             (txType === 'withdrawal' && reason === 'bet') ||
                             isTurboStarsBet || isBCBetTx;
                const isWin = txType === 'win' || 
                             (txType === 'deposit' && reason === 'win');
                const isSettle = (txType === 'deposit' && reason === 'settle') || isTurboStarsSettle || isBCResultTx;
                const isResettle = reason === 'resettle';
                const isCancelSettle = reason === 'cancelsettle';
                const isRollback = isTurboStarsRollback || isBCRollback || txType === 'rollback';
                const txAmount = parseFloat(tx.amount || 0);
                
                // Get TurboStars result type for settle transactions
                const turboStarsResultType = tx.context?.additionalContext?.resultType;
                // BetConstruct state/delta for label tinting
                const bcDelta = bcSignedDelta(tx);
                const bcState = Number(tx.context?.additionalContext?.bcBetState);
                const bcStateLabel = bcStateToResult(bcState); // Win/Lose/Void/Cashout/Pending
                const bcIsVoid = tx.context?.additionalContext?.isVoid === true ||
                                 tx.context?.additionalContext?.isVoid === 'true' ||
                                 bcState === 2;
                const bcIsResettled = tx.context?.additionalContext?.isResettled === true ||
                                       tx.context?.additionalContext?.isResettled === 'true';
                const isCashout = turboStarsResultType === 'cashout' || reason.includes('cashout') || bcState === 5;
                
                const getTransactionTypeLabel = () => {
                  if (isRollback) return t('betHistory.rollback', 'Cancelled/Refunded');
                  if (isBet) return t('betHistory.bet', 'Bet');
                  if (isSettle) {
                    if (isCashout) return t('betHistory.cashout', 'Cashed Out');
                    if (turboStarsResultType === 'won') return t('betHistory.settlementWon', 'Settlement (Won)');
                    if (turboStarsResultType === 'lost') return t('betHistory.settlementLost', 'Settlement (Lost)');
                    if (isBCResultTx) {
                      const prefix = bcIsResettled ? t('betHistory.resettle', 'Resettlement') : t('betHistory.settle', 'Settle');
                      if (bcStateLabel === 'Win') return `${prefix} (${t('betHistory.won', 'Won')})`;
                      if (bcStateLabel === 'Lose') return `${prefix} (${t('betHistory.lost', 'Lost')})`;
                      if (bcStateLabel === 'Cashout') return t('betHistory.cashout', 'Cashed Out');
                      if (bcStateLabel === 'Void' || bcIsVoid) return `${prefix} (${t('betHistory.void', 'Void')})`;
                      if (bcStateLabel === 'Pending') return t('betHistory.pending', 'Pending');
                      return t('betHistory.betconstructResult', 'Result');
                    }
                    return t('betHistory.settle', 'Settle');
                  }
                  if (isResettle) return t('betHistory.resettle', 'Resettlement');
                  if (isCancelSettle) return t('betHistory.cancelSettle', 'Settlement Cancelled');
                  if (isWin) {
                    if (txAmount === 0) return t('betHistory.roundClosed', 'Round Closed');
                    return t('betHistory.win', 'Win');
                  }
                  if (txType === 'deposit') return t('betHistory.deposit', 'Deposit');
                  if (txType === 'withdrawal') return t('betHistory.withdrawal', 'Withdrawal');
                  return tx.type || 'Transaction';
                };
                
                const getTransactionColor = () => {
                  if (isRollback) return 'text-gray-600 dark:text-gray-400';
                  if (isBet) return 'text-blue-600 dark:text-blue-400';
                  if (isSettle) {
                    if (isCashout) return 'text-cyan-600 dark:text-cyan-400';
                    if (turboStarsResultType === 'won') return 'text-green-600 dark:text-green-400';
                    if (turboStarsResultType === 'lost') return 'text-red-600 dark:text-red-400';
                    if (isBCResultTx) {
                      if (bcStateLabel === 'Win') return 'text-green-600 dark:text-green-400';
                      if (bcStateLabel === 'Lose') return 'text-red-600 dark:text-red-400';
                      if (bcStateLabel === 'Cashout') return 'text-cyan-600 dark:text-cyan-400';
                      if (bcStateLabel === 'Void') return 'text-gray-600 dark:text-gray-400';
                      return bcDelta > 0 ? 'text-green-600 dark:text-green-400' :
                             bcDelta < 0 ? 'text-red-600 dark:text-red-400' :
                             'text-gray-600 dark:text-gray-400';
                    }
                    if (txAmount > 0) return 'text-green-600 dark:text-green-400';
                    return txAmount >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
                  }
                  if (isResettle) return tx.type?.toLowerCase() === 'deposit' ? 'text-purple-600 dark:text-purple-400' : 'text-orange-600 dark:text-orange-400';
                  if (isCancelSettle) return 'text-amber-600 dark:text-amber-400';
                  if (isWin && txAmount > 0) return 'text-green-600 dark:text-green-400';
                  if (isWin && txAmount === 0) return 'text-gray-600 dark:text-gray-400';
                  return 'text-gray-600 dark:text-gray-400';
                };
                
                const getIconColor = () => {
                  if (isRollback) return 'bg-gray-500';
                  if (isBet) return 'bg-blue-500';
                  if (isWin && txAmount > 0) return 'bg-green-500';
                  if (isSettle) {
                    if (isCashout) return 'bg-cyan-500';
                    if (turboStarsResultType === 'won') return 'bg-emerald-500';
                    if (turboStarsResultType === 'lost') return 'bg-red-500';
                    if (isBCResultTx) {
                      if (bcStateLabel === 'Win') return 'bg-emerald-500';
                      if (bcStateLabel === 'Lose') return 'bg-red-500';
                      if (bcStateLabel === 'Cashout') return 'bg-cyan-500';
                      if (bcStateLabel === 'Void') return 'bg-gray-500';
                      return bcDelta > 0 ? 'bg-emerald-500' : bcDelta < 0 ? 'bg-red-500' : 'bg-gray-500';
                    }
                    return txAmount > 0 ? 'bg-emerald-500' : 'bg-red-500';
                  }
                  if (isResettle) return 'bg-purple-500';
                  if (isCancelSettle) return 'bg-amber-500';
                  return 'bg-gray-500';
                };
                
                // Format date for this transaction
                const txDate = new Date(tx.timestampISO || tx.timestamp || tx.timestampNumber);
                const txNow = new Date();
                const txIsToday = format(txNow, 'yyyy-MM-dd') === format(txDate, 'yyyy-MM-dd');
                const txIsYesterday = format(new Date(txNow.getTime() - 24 * 60 * 60 * 1000), 'yyyy-MM-dd') === format(txDate, 'yyyy-MM-dd');
                const txIsThisYear = txNow.getFullYear() === txDate.getFullYear();
                
                let txDateLabel;
                if (txIsToday) {
                  txDateLabel = t('betHistory.today', 'Today');
                } else if (txIsYesterday) {
                  txDateLabel = t('betHistory.yesterday', 'Yesterday');
                } else if (txIsThisYear) {
                  txDateLabel = format(txDate, 'MMM dd');
                } else {
                  txDateLabel = format(txDate, 'MMM dd, yyyy');
                }
                
                const txFormattedTime = format(txDate, 'HH:mm:ss');
                const txFullDateTime = format(txDate, 'PPpp');
                
                return (
                  <div key={tx.transactionId} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2.5 border border-gray-200 dark:border-gray-600">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1 ${getIconColor()}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className={`font-semibold text-xs ${getTransactionColor()}`}>
                              {getTransactionTypeLabel()}
                            </span>
                            {/* Date/Time */}
                            <div 
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 cursor-help"
                              title={txFullDateTime}
                            >
                              <CalendarIcon className="w-2.5 h-2.5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                              <span className="text-[9px] text-blue-700 dark:text-blue-300 font-medium whitespace-nowrap">
                                {txDateLabel} • {txFormattedTime}
                              </span>
                            </div>
                            {/* Status */}
                            <span className={`text-[9px] capitalize ${
                              tx.status === 'completed' ? 'text-green-600 dark:text-green-400' :
                              tx.status === 'pending' ? 'text-yellow-600 dark:text-yellow-400' :
                              'text-gray-600 dark:text-gray-400'
                            }`}>
                              {tx.status}
                            </span>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(tx.transactionId, tx.transactionId);
                            }}
                            className={`text-[10px] font-mono transition-all cursor-pointer text-left ${
                              copiedId === tx.transactionId
                                ? 'text-green-600 dark:text-green-400 font-semibold'
                                : 'text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400'
                            }`}
                            title={copiedId === tx.transactionId ? t('betHistory.copied', 'Copied!') : t('betHistory.clickToCopy', 'Click to copy')}
                          >
                            {copiedId === tx.transactionId ? '✓ ' : ''}
                            {tx.transactionId.length > 30 ? `${tx.transactionId.substring(0, 28)}...` : tx.transactionId}
                          </button>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className={`font-bold text-sm ${getTransactionColor()}`}>
                          {(() => {
                            // For BetConstruct results, the signed delta is what
                            // actually moved in the wallet (resettlements can be
                            // deposits with a negative delta). Use it for sign
                            // and magnitude so users see the real direction.
                            if (isBCResultTx) {
                              if (bcDelta > 0) return `+${formatCurrency(Math.abs(bcDelta), tx.currency)}`;
                              if (bcDelta < 0) return `-${formatCurrency(Math.abs(bcDelta), tx.currency)}`;
                              return formatCurrency(0, tx.currency);
                            }
                            const sign = isBet ? '-' :
                              ((isWin && txAmount > 0) || (isSettle && txAmount > 0)) ? '+' : '';
                            return `${sign}${formatCurrency(txAmount, tx.currency)}`;
                          })()}
                        </div>
                        {/* Resettlement / Void / Freebet / Tax indicators for BetConstruct */}
                        {isBCResultTx && (bcIsResettled || bcIsVoid) && (
                          <div className="mt-0.5 flex items-center justify-end gap-1">
                            {bcIsResettled && (
                              <span className="text-[9px] px-1 py-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 font-medium uppercase tracking-wider">
                                {t('betHistory.resettled', 'Resettled')}
                              </span>
                            )}
                            {bcIsVoid && (
                              <span className="text-[9px] px-1 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium uppercase tracking-wider">
                                {t('betHistory.void', 'Void')}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {tx.balanceAfter && (
                      <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                        <div className="flex justify-between items-center">
                          <span className="text-[10px] text-gray-500 dark:text-gray-400">
                            {t('betHistory.balanceAfter', 'Balance')}
                          </span>
                          <span className="text-[10px] font-medium text-gray-700 dark:text-gray-300">
                            {formatCurrency(
                              (parseFloat(tx.balanceAfter.cash || 0) + 
                               parseFloat(tx.balanceAfter.bonus || 0) + 
                               parseFloat(tx.balanceAfter.locked || 0)), 
                              tx.currency
                            )}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Round Details - Compact Mobile Layout */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-2.5">
            <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wider">
              {t('betHistory.roundDetails', 'Round Details')}
            </h4>
            <div className="space-y-1.5">
              {/* Round ID */}
              {roundId && (
                <div className="flex items-start justify-between gap-2">
                  <span className="text-[10px] text-gray-500 dark:text-gray-400 flex-shrink-0 w-20">
                    {t('betHistory.roundId', 'Round ID')}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard(roundId, 'roundId');
                    }}
                    className={`text-[10px] font-mono transition-all cursor-pointer text-right break-all flex-1 ${
                      copiedId === 'roundId'
                        ? 'text-green-600 dark:text-green-400 font-semibold'
                        : 'text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400'
                    }`}
                    title={copiedId === 'roundId' ? t('betHistory.copied', 'Copied!') : t('betHistory.clickToCopy', 'Click to copy')}
                  >
                    {copiedId === 'roundId' ? '✓ ' : ''}
                    {roundId.length > 30 ? `${roundId.substring(0, 28)}...` : roundId}
                  </button>
                </div>
              )}
              
              {/* Bet ID for sports */}
              {primaryTransaction.context?.betId && (
                <div className="flex items-start justify-between gap-2">
                  <span className="text-[10px] text-gray-500 dark:text-gray-400 flex-shrink-0 w-20">
                    {t('betHistory.betId', 'Bet ID')}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard(primaryTransaction.context.betId, 'betId');
                    }}
                    className={`text-[10px] font-mono transition-all cursor-pointer text-right break-all flex-1 ${
                      copiedId === 'betId'
                        ? 'text-green-600 dark:text-green-400 font-semibold'
                        : 'text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400'
                    }`}
                    title={copiedId === 'betId' ? t('betHistory.copied', 'Copied!') : t('betHistory.clickToCopy', 'Click to copy')}
                  >
                    {copiedId === 'betId' ? '✓ ' : ''}
                    {primaryTransaction.context.betId.length > 30 ? `${primaryTransaction.context.betId.substring(0, 28)}...` : primaryTransaction.context.betId}
                  </button>
                </div>
              )}
              
              {/* Duration & Currency on same line */}
              <div className="flex items-center justify-between gap-4">
                {transactions.length > 1 && (
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">
                      {t('betHistory.duration', 'Duration')}
                    </span>
                    <span className="text-[10px] font-medium text-gray-700 dark:text-gray-300">
                      {roundStats.duration > 1000 ? `${Math.round(roundStats.duration / 1000)}s` : `${roundStats.duration}ms`}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-gray-500 dark:text-gray-400">
                    {t('betHistory.currency', 'Currency')}
                  </span>
                  <span className="text-[10px] font-semibold text-gray-700 dark:text-gray-300">
                    {roundStats.currency}
                  </span>
                </div>
              </div>

              {/* Game Code */}
              {gameInfo.rawName && (
                <div className="flex items-start justify-between gap-2">
                  <span className="text-[10px] text-gray-500 dark:text-gray-400 flex-shrink-0 w-20">
                    {t('betHistory.game', 'Game')}
                  </span>
                  <span className="font-mono text-[10px] text-gray-700 dark:text-gray-300 text-right flex-1">
                    {gameInfo.rawName}
                  </span>
                </div>
              )}
              
              {/* Player ID */}
              <div className="flex items-start justify-between gap-2">
                <span className="text-[10px] text-gray-500 dark:text-gray-400 flex-shrink-0 w-20">
                  {t('betHistory.player', 'Player')}
                </span>
                <span className="font-mono text-[10px] text-gray-700 dark:text-gray-300 text-right flex-1">
                  {primaryTransaction.playerId}
                </span>
              </div>
            </div>
          </div>

          {/* Final Balance Breakdown - Compact Mobile Layout */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-2.5">
            <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wider">
              {t('betHistory.finalBalance', 'Final Balance')}
            </h4>
            <div className="space-y-1">
              <div className="flex justify-between items-center">
                <span className="text-[10px] text-gray-500 dark:text-gray-400">
                  {t('betHistory.cash', 'Cash')}
                </span>
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  {formatCurrency(balanceBreakdown.cash, roundStats.currency, { compact: true })}
                </span>
              </div>
              {balanceBreakdown.bonus > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-[10px] text-gray-500 dark:text-gray-400">
                    {t('betHistory.bonus', 'Bonus')}
                  </span>
                  <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                    {formatCurrency(balanceBreakdown.bonus, roundStats.currency, { compact: true })}
                  </span>
                </div>
              )}
              {balanceBreakdown.locked > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-[10px] text-gray-500 dark:text-gray-400">
                    {t('betHistory.locked', 'Locked')}
                  </span>
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                    {formatCurrency(balanceBreakdown.locked, roundStats.currency, { compact: true })}
                  </span>
                </div>
              )}
              <div className="pt-1.5 mt-1.5 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">
                  {t('betHistory.total', 'Total')}
                </span>
                <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
                  {formatCurrency(totalBalance, roundStats.currency, { compact: true })}
                </span>
              </div>
            </div>
          </div>

          {/* Description if available */}
          {gameInfo.description && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2.5">
              <h4 className="text-xs font-semibold text-blue-700 dark:text-blue-300 mb-1.5">{t('betHistory.description', 'Description')}</h4>
              <p className="text-xs text-blue-600 dark:text-blue-400">
                {gameInfo.description}
              </p>
            </div>
          )}

          {/* Sports Bet Details */}
          {sportsBetInfo && sportsBetInfo.items.length > 0 && (
            <div className={`relative rounded-lg p-3 ${
              sportsBetInfo.provider === 'Sport'
                ? 'bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 dark:from-amber-900/20 dark:via-orange-900/20 dark:to-red-900/20 ring-1 ring-orange-200/60 dark:ring-orange-800/40'
                : 'bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20'
            }`}>
              {/* Link connector to round transactions */}
              {sportsBetInfo.betType === 'Express' && sportsBetInfo.items.length > 1 && (
                <div className={`absolute left-4 top-16 bottom-4 w-0.5 opacity-30 ${
                  sportsBetInfo.provider === 'Sport'
                    ? 'bg-gradient-to-b from-orange-300 to-red-300 dark:from-orange-600 dark:to-red-600'
                    : 'bg-gradient-to-b from-blue-300 to-indigo-300 dark:from-blue-600 dark:to-indigo-600'
                }`} />
              )}
              
              <h4 className={`text-sm font-semibold mb-3 flex items-center gap-2 flex-wrap ${
                sportsBetInfo.provider === 'Sport'
                  ? 'text-orange-700 dark:text-orange-300'
                  : 'text-blue-700 dark:text-blue-300'
              }`}>
                <TrophyIcon className="w-4 h-4" />
                {t('betHistory.sportsBetDetails', 'Sports Bet Details')}
                {sportsBetInfo.provider === 'Sport' && sportsBetInfo.isFreebet && (
                  <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-gradient-to-r from-yellow-300 to-amber-300 dark:from-yellow-600 dark:to-amber-600 text-yellow-900 dark:text-yellow-50 uppercase tracking-wider shadow-sm">
                    {t('betHistory.freebet', 'Freebet')}
                  </span>
                )}
                {sportsBetInfo.provider === 'Sport' && sportsBetInfo.isResettled && (
                  <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-purple-200 dark:bg-purple-800 text-purple-800 dark:text-purple-200 uppercase tracking-wider">
                    {t('betHistory.resettled', 'Resettled')}
                  </span>
                )}
                {sportsBetInfo.provider === 'Sport' && sportsBetInfo.isPartial && (
                  <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 uppercase tracking-wider">
                    {t('betHistory.partial', 'Partial')}
                  </span>
                )}
                {sportsBetInfo.provider === 'Sport' && sportsBetInfo.winningTax > 0 && (
                  <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 uppercase tracking-wider" title={t('betHistory.winningTaxTooltip', 'Tax withheld on winnings')}>
                    {t('betHistory.tax', 'Tax')}: {sportsBetInfo.winningTax}
                  </span>
                )}
                {sportsBetInfo.provider === 'Sport' && sportsBetInfo.betTypeLabel && sportsBetInfo.betTypeLabel !== 'Single' && sportsBetInfo.betTypeLabel !== 'Express' && (
                  <span className="ml-auto px-2 py-0.5 rounded-md text-[10px] font-semibold bg-orange-200/70 dark:bg-orange-800/40 text-orange-800 dark:text-orange-200 uppercase tracking-wider">
                    {sportsBetInfo.betTypeLabel}
                  </span>
                )}
              </h4>
              
              {/* Bet Summary with Win/Loss Progress for Multi-bets */}
              <div className="space-y-3 mb-4">
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                    sportsBetInfo.betType === 'Express'
                      ? (sportsBetInfo.provider === 'Sport'
                          ? 'bg-orange-200 dark:bg-orange-800/60 text-orange-800 dark:text-orange-200'
                          : 'bg-purple-200 dark:bg-purple-800 text-purple-800 dark:text-purple-200')
                      : (sportsBetInfo.provider === 'Sport'
                          ? 'bg-amber-200 dark:bg-amber-800/60 text-amber-800 dark:text-amber-200'
                          : 'bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200')
                  }`}>
                    {sportsBetInfo.provider === 'Sport' && sportsBetInfo.betTypeLabel
                      ? (sportsBetInfo.betTypeLabel === 'Express'
                          ? t('betHistory.accumulator', 'Accumulator')
                          : sportsBetInfo.betTypeLabel)
                      : sportsBetInfo.betType === 'Express'
                        ? t('betHistory.accumulator', 'Accumulator')
                        : t('betHistory.single', 'Single')}
                  </span>
                  <span className="text-xs sm:text-sm font-bold text-gray-700 dark:text-gray-300">
                    {t('betHistory.odds', 'Odds')}: <span className={sportsBetInfo.provider === 'Sport' ? 'text-orange-600 dark:text-orange-400' : 'text-blue-600 dark:text-blue-400'}>{sportsBetInfo.betOdds.toFixed(2)}</span>
                  </span>
                  {sportsBetInfo.betResult !== 'Unknown' && (
                    <span className={`ml-auto px-2 py-1 rounded-md text-xs font-medium ${
                      sportsBetInfo.betResult === 'Win'
                        ? 'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200'
                        : sportsBetInfo.betResult === 'Lose'
                        ? 'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200'
                        : sportsBetInfo.betResult === 'Cashout'
                        ? 'bg-cyan-200 dark:bg-cyan-800 text-cyan-800 dark:text-cyan-200'
                        : sportsBetInfo.betResult === 'Void'
                        ? 'bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200'
                        : sportsBetInfo.betResult === 'Cancelled'
                        ? 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 line-through'
                        : 'bg-gray-200 dark:bg-gray-800 text-gray-800 dark:text-gray-200'
                    }`}>
                      {sportsBetInfo.betResult === 'Win'
                        ? t('betHistory.winner', 'Winner!')
                        : sportsBetInfo.betResult === 'Lose'
                        ? t('betHistory.notWon', 'Not Won')
                        : sportsBetInfo.betResult === 'Cashout'
                        ? t('betHistory.cashedOut', 'Cashed Out')
                        : sportsBetInfo.betResult === 'Void'
                        ? t('betHistory.void', 'Void / Returned')
                        : sportsBetInfo.betResult === 'Cancelled'
                        ? t('betHistory.cancelled', 'Cancelled')
                        : sportsBetInfo.betResult}
                    </span>
                  )}
                </div>
                
                {/* Win/Loss Summary for all settled bets */}
                {sportsBetInfo.isSettled && sportsBetInfo.items.some(item => item.result !== 'Unknown') && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                    <h5 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2.5 uppercase tracking-wider">
                      {t('betHistory.selectionResults', 'Selection Results')}
                    </h5>
                    <div className="space-y-1.5">
                      {sportsBetInfo.items.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 rounded-md bg-gray-50 dark:bg-gray-900/50">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            {item.result === 'Win' ? (
                              <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                                <CheckCircleIcon className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                              </div>
                            ) : item.result === 'Lose' ? (
                              <div className="w-5 h-5 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                                <XCircleIcon className="w-3.5 h-3.5 text-red-600 dark:text-red-400" />
                              </div>
                            ) : (
                              <div className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                                <ClockIcon className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {item.event.sport} • {item.event.tournament}
                              </div>
                              <div className="text-xs font-medium text-gray-900 dark:text-gray-100">
                                {item.selections?.[0]?.selection || item.event.name}
                              </div>
                            </div>
                          </div>
                          <span className={`ml-2 px-2 py-1 rounded text-[10px] font-medium flex-shrink-0 ${
                            item.result === 'Win'
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                              : item.result === 'Lose'
                              ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                          }`}>
                            {item.result === 'Win' 
                              ? t('betHistory.won', 'Won') 
                              : item.result === 'Lose' 
                              ? t('betHistory.lost', 'Lost') 
                              : t('betHistory.pending', 'Pending')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Win Progress Bar for Multi-bets with settled results */}
                {sportsBetInfo.betType === 'Express' && sportsBetInfo.items.length > 1 && sportsBetInfo.betResult !== 'Unknown' && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                        {t('betHistory.selectionProgress', 'Selection Progress')}
                      </span>
                      <span className="text-xs font-bold text-gray-700 dark:text-gray-300">
                        {sportsBetInfo.items.filter(item => item.result === 'Win' || item.selections?.[0]?.result === 'Win').length} / {sportsBetInfo.items.length}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all duration-500 ${
                          sportsBetInfo.betResult === 'Win' 
                            ? 'bg-gradient-to-r from-green-400 to-green-600' 
                            : 'bg-gradient-to-r from-red-400 to-red-600'
                        }`}
                        style={{ 
                          width: `${(sportsBetInfo.items.filter(item => 
                            item.result === 'Win' || item.selections?.[0]?.result === 'Win'
                          ).length / sportsBetInfo.items.length) * 100}%` 
                        }}
                      />
                    </div>
                    <div className="flex items-center gap-3 mt-2 text-[10px]">
                      <div className="flex items-center gap-1">
                        <CheckCircleIcon className="w-3 h-3 text-green-500" />
                        <span className="text-green-600 dark:text-green-400 font-medium">
                          {sportsBetInfo.items.filter(item => item.result === 'Win' || item.selections?.[0]?.result === 'Win').length} {t('betHistory.won', 'Won')}
                        </span>
                      </div>
                      {sportsBetInfo.items.filter(item => item.result === 'Lose' || item.selections?.[0]?.result === 'Lose').length > 0 && (
                        <div className="flex items-center gap-1">
                          <XCircleIcon className="w-3 h-3 text-red-500" />
                          <span className="text-red-600 dark:text-red-400 font-medium">
                            {sportsBetInfo.items.filter(item => item.result === 'Lose' || item.selections?.[0]?.result === 'Lose').length} {t('betHistory.lost', 'Lost')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Bet Selections with visual linking */}
              <div className="relative space-y-2">
                {sportsBetInfo.items.map((item, idx) => (
                  <div key={idx} className="relative">
                    {/* Connector dot for multi-bet */}
                    {sportsBetInfo.betType === 'Express' && sportsBetInfo.items.length > 1 && (
                      <div className="absolute left-1 top-1/2 -translate-y-1/2 w-2 h-2 bg-blue-400 dark:bg-blue-500 rounded-full z-10" />
                    )}
                    
                    <div className={`bg-white dark:bg-gray-800 rounded-md overflow-hidden border ${
                      sportsBetInfo.betType === 'Express' && sportsBetInfo.items.length > 1 ? 'ml-6' : ''
                    } ${
                      item.result === 'Win' 
                        ? 'border-green-300 dark:border-green-700'
                        : item.result === 'Lose'
                        ? 'border-red-300 dark:border-red-700'
                        : 'border-gray-200 dark:border-gray-700'
                    }`}>
                      {/* Result Banner for settled bets - Extra Thin */}
                      {item.result !== 'Unknown' && (
                        <div className={`px-2.5 py-1 ${
                          item.result === 'Win'
                            ? 'bg-green-500 text-white'
                            : item.result === 'Lose'
                            ? 'bg-red-500 text-white'
                            : 'bg-gray-500 text-white'
                        }`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1">
                              {item.result === 'Win' ? (
                                <CheckCircleIcon className="w-3 h-3 flex-shrink-0" />
                              ) : item.result === 'Lose' ? (
                                <XCircleIcon className="w-3 h-3 flex-shrink-0" />
                              ) : (
                                <ClockIcon className="w-3 h-3 flex-shrink-0" />
                              )}
                              <span className="text-[10px] font-medium">
                                {item.result === 'Win' 
                                  ? t('betHistory.selectionWon', 'Selection Won') 
                                  : item.result === 'Lose' 
                                  ? t('betHistory.selectionLost', 'Selection Lost') 
                                  : t('betHistory.selectionPending', 'Pending')}
                              </span>
                            </div>
                            {sportsBetInfo.betType === 'Express' && (
                              <span className="text-[10px] font-medium">
                                {idx + 1}/{sportsBetInfo.items.length}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <div className="p-2.5">
                        {/* Event Header */}
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                              <span className="text-[10px] px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded font-medium text-gray-600 dark:text-gray-400">
                                {item.event.sport}
                              </span>
                              {item.event.stage && (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                  item.event.stage === 'Live'
                                    ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 animate-pulse'
                                    : item.event.stage === 'Prematch'
                                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                                }`}>
                                  {item.event.stage === 'Live' 
                                    ? t('betHistory.live', 'LIVE')
                                    : item.event.stage === 'Prematch'
                                    ? t('betHistory.prematch', 'Pre-match')
                                    : item.event.stage}
                                </span>
                              )}
                              {item.result === 'Unknown' && sportsBetInfo.items.length > 1 && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded font-medium">
                                  {idx + 1}/{sportsBetInfo.items.length}
                                </span>
                              )}
                            </div>
                            
                            <div className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                              {item.event.competitors.length > 0
                                ? item.event.competitors.map((team, i) => {
                                  const isSelectedTeam = item.selections?.[0]?.selection === team;
                                  const isWinningTeam = isSelectedTeam && item.result === 'Win';
                                  const isLosingTeam = isSelectedTeam && item.result === 'Lose';
                                  
                                  return (
                                    <span key={i}>
                                      {i > 0 && <span className="mx-1 text-gray-400 text-xs">vs</span>}
                                      <span className={`${
                                        isWinningTeam 
                                          ? 'text-green-600 dark:text-green-400 font-semibold'
                                          : isLosingTeam
                                          ? 'text-red-600 dark:text-red-400 opacity-75'
                                          : isSelectedTeam
                                          ? 'font-semibold text-blue-600 dark:text-blue-400'
                                          : ''
                                      }`}>
                                        {team}
                                        {isWinningTeam && ' ✓'}
                                      </span>
                                    </span>
                                  );
                                })
                                : item.event.name}
                            </div>
                            
                            <div className="text-[10px] text-gray-500 dark:text-gray-400 space-y-0.5">
                              <div>{item.event.tournament}</div>
                              <div>{item.event.category}</div>
                              {item.event.startTime && (
                                <div className="flex items-center gap-1">
                                  <CalendarIcon className="w-3 h-3" />
                                  {format(item.event.startTime, 'MMM dd, yyyy HH:mm')}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* A multi-bet spans several leagues, so each leg gets its own shortcut */}
                          {canSetLimits && cleanUsername && (item.event.tournament || item.event.sport) && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setLimitBet({
                                  isSports: true,
                                  leagueName: item.event.tournament,
                                  sportName: item.event.sport
                                });
                              }}
                              className="shrink-0 self-start inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/40 active:bg-amber-200 dark:active:bg-amber-900/60 transition-colors text-[11px] font-semibold touch-manipulation cursor-pointer relative z-10"
                              title={`${t('limitScope.limitLeague', 'Limit league')}: ${item.event.tournament || item.event.sport}`}
                            >
                              <ShieldExclamationIcon className="w-3.5 h-3.5 flex-shrink-0" />
                              {item.event.tournament
                                ? t('limitScope.limitLeague', 'Limit league')
                                : t('limitScope.limitSport', 'Limit sport')}
                            </button>
                          )}
                        </div>
                      
                        {/* Selection Details */}
                        <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                          {item.selections.map((selection, sidx) => (
                            <div key={sidx} className="flex items-center justify-between text-xs py-1">
                              <div className="flex-1 min-w-0">
                                <div className="text-[10px] text-gray-500 dark:text-gray-400">
                                  {selection.market}
                                </div>
                                <div className={`font-medium ${
                                  selection.result === 'Win'
                                    ? 'text-green-600 dark:text-green-400'
                                    : selection.result === 'Lose'
                                    ? 'text-red-600 dark:text-red-400'
                                    : 'text-gray-900 dark:text-gray-100'
                                }`}>
                                  {selection.selection}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-600 dark:text-gray-400">
                                  @{selection.odds.toFixed(2)}
                                </span>
                                {selection.result === 'Win' && (
                                  <CheckCircleIcon className="w-4 h-4 text-green-500" />
                                )}
                                {selection.result === 'Lose' && (
                                  <XCircleIcon className="w-4 h-4 text-red-500" />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Additional Context from primary transaction */}
          {primaryTransaction.context?.additionalContext && !roundStats.isPlatformSport && (
            <div className="bg-gray-100 dark:bg-gray-800/50 rounded-lg p-2.5">
              <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wider">{t('betHistory.additionalDetails', 'Additional Details')}</h4>
              <div className="space-y-1">
                {Object.entries(primaryTransaction.context.additionalContext).map(([key, value]) => (
                  <div key={key} className="flex justify-between items-center">
                    <span className="text-[10px] text-gray-500 dark:text-gray-400 capitalize">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      {typeof value === 'boolean' ? (value ? t('common.yes', 'Yes') : t('common.no', 'No')) : String(value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {limitBet && (
        <LimitScopeModal
          isOpen
          onClose={() => setLimitBet(null)}
          bet={limitBet}
          player={limitPlayer}
          userTree={userTree}
          currency={roundStats.currency}
        />
      )}
    </div>
  );
};

// Interactive Balance Chart Component
const BalanceChart = ({ transactions }) => {
  const { t } = useTranslation();
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Calculate balance progression
  const balanceData = useMemo(() => {
    if (!transactions || transactions.length === 0) return [];
    
    // Sort transactions by timestamp (oldest first for chart)
    const sorted = [...transactions].sort((a, b) => {
      const timestampA = getTransactionTimestamp(a);
      const timestampB = getTransactionTimestamp(b);
      return timestampA - timestampB;
    });
    
    return sorted.map(tx => ({
      timestamp: getTransactionTimestamp(tx),
      balance: parseFloat(tx.balanceAfter?.cash || 0),
      type: tx.type,
      amount: parseFloat(tx.amount || 0),
      playerId: tx.playerId
    }));
  }, [transactions]);
  
  if (balanceData.length === 0) return null;
  
  // Find min and max for scaling
  const minBalance = Math.min(...balanceData.map(d => d.balance));
  const maxBalance = Math.max(...balanceData.map(d => d.balance));
  const range = maxBalance - minBalance || 1;
  
  // Create SVG dimensions - larger when expanded
  const width = 300;
  const height = isExpanded ? 120 : 60;
  const padding = 5;
  
  // Create points for the line
  const points = balanceData.map((d, i) => {
    const x = padding + ((i / (balanceData.length - 1 || 1)) * (width - 2 * padding));
    const y = padding + (height - 2 * padding) - ((d.balance - minBalance) / range) * (height - 2 * padding);
    return { x, y, data: d, index: i };
  });
  
  const pathString = points.map(p => `${p.x},${p.y}`).join(' ');
  
  const isPositive = balanceData[balanceData.length - 1]?.balance >= balanceData[0]?.balance;
  const changeAmount = balanceData[balanceData.length - 1]?.balance - balanceData[0]?.balance;
  const changePercent = balanceData[0]?.balance ? (changeAmount / balanceData[0].balance * 100).toFixed(1) : 0;
  
  return (
    <div 
      className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 transition-all duration-300 ${
        isExpanded ? 'p-4' : 'p-3'
      }`}
      onClick={() => setIsExpanded(!isExpanded)}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <h3 className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
            {t('betHistory.balanceTrend', 'Balance Trend')}
          </h3>
          <ChevronDownIcon className={`w-3 h-3 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </div>
        <div className="text-right">
          <div className={`text-xs sm:text-sm font-bold ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
            {isPositive ? '↑' : '↓'} {formatCurrency(changeAmount)}
          </div>
          <div className={`text-[10px] ${isPositive ? 'text-emerald-500' : 'text-red-500'}`}>
            {isPositive ? '+' : ''}{changePercent}%
          </div>
        </div>
      </div>
      
      {/* Chart */}
      <div className="relative">
        <svg 
          width="100%" 
          height={height} 
          viewBox={`0 0 ${width} ${height}`} 
          className="w-full cursor-pointer"
          onMouseLeave={() => setHoveredIndex(null)}
        >
          {/* Grid lines when expanded */}
          {isExpanded && (
            <>
              {[0, 0.25, 0.5, 0.75, 1].map(fraction => (
                <line
                  key={fraction}
                  x1={padding}
                  y1={padding + (height - 2 * padding) * fraction}
                  x2={width - padding}
                  y2={padding + (height - 2 * padding) * fraction}
                  stroke="currentColor"
                  strokeOpacity="0.1"
                  strokeDasharray="2,2"
                />
              ))}
            </>
          )}
          
          {/* Area fill */}
          <polygon
            points={`${points[0].x},${height - padding} ${pathString} ${points[points.length - 1].x},${height - padding}`}
            fill={isPositive ? '#10b981' : '#ef4444'}
            fillOpacity="0.1"
          />
          
          {/* Main line */}
          <polyline
            points={pathString}
            fill="none"
            stroke={isPositive ? '#10b981' : '#ef4444'}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          
          {/* Interactive dots when expanded */}
          {isExpanded && points.map((point, i) => (
            <g key={i}>
              <circle
                cx={point.x}
                cy={point.y}
                r="3"
                fill={isPositive ? '#10b981' : '#ef4444'}
                opacity={hoveredIndex === i ? 1 : 0.3}
                onMouseEnter={() => setHoveredIndex(i)}
                style={{ cursor: 'pointer' }}
              />
              {/* Invisible larger hit area */}
              <circle
                cx={point.x}
                cy={point.y}
                r="10"
                fill="transparent"
                onMouseEnter={() => setHoveredIndex(i)}
                style={{ cursor: 'pointer' }}
              />
            </g>
          ))}
        </svg>
        
        {/* Tooltip */}
        {hoveredIndex !== null && isExpanded && (
          <div className="absolute top-0 left-0 bg-gray-900 text-white text-[10px] px-2 py-1 rounded shadow-lg pointer-events-none z-10"
               style={{
                 transform: `translate(${points[hoveredIndex].x - 30}px, ${points[hoveredIndex].y - 35}px)`
               }}>
            <div className="font-bold">{formatCurrency(balanceData[hoveredIndex].balance)}</div>
            <div className="text-gray-300">{format(new Date(balanceData[hoveredIndex].timestamp), 'MMM dd HH:mm')}</div>
          </div>
        )}
      </div>
      
      {/* Stats when expanded */}
      {isExpanded && (
        <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
          <div className="text-center">
            <div className="text-[10px] text-gray-500 dark:text-gray-400">{t('betHistory.startBalance', 'Start')}</div>
            <div className="text-xs font-medium">{formatCurrency(balanceData[0]?.balance)}</div>
          </div>
          <div className="text-center">
            <div className="text-[10px] text-gray-500 dark:text-gray-400">{t('betHistory.currentBalance', 'Current')}</div>
            <div className="text-xs font-medium">{formatCurrency(balanceData[balanceData.length - 1]?.balance)}</div>
          </div>
          <div className="text-center">
            <div className="text-[10px] text-gray-500 dark:text-gray-400">{t('betHistory.transactions', 'Transactions')}</div>
            <div className="text-xs font-medium">{balanceData.length}</div>
          </div>
        </div>
      )}
    </div>
  );
};

// Filter reducer for consolidated state management
const filterReducer = (state, action) => {
  switch (action.type) {
    case 'SET_DATE_RANGE':
      return { ...state, dateRange: action.payload };
    case 'SET_CUSTOM_DATES':
      return { ...state, customStartDate: action.payload.start, customEndDate: action.payload.end };
    case 'SET_ACTIVE_TAB':
      return { ...state, activeTab: action.payload, sportsResultFilter: 'all' };
    case 'SET_PROVIDER_FILTER':
      return { ...state, providerFilter: action.payload };
    case 'SET_SORT_ORDER':
      return { ...state, sortOrder: action.payload };
    case 'SET_SPORTS_RESULT_FILTER':
      return { ...state, sportsResultFilter: action.payload };
    case 'SET_AMOUNT_FILTER':
      return { ...state, amountFilter: action.payload };
    case 'UPDATE_AMOUNT_FILTER':
      return { ...state, amountFilter: { ...state.amountFilter, ...action.payload } };
    case 'SET_GAME_FILTER':
      return { ...state, gameFilter: action.payload };
    case 'SET_CURRENCY_FILTER':
      return { ...state, currencyFilter: action.payload };
    case 'SET_SEARCH_TERM':
      return { ...state, searchTerm: action.payload };
    case 'CLEAR_ALL_FILTERS':
      return {
        ...state,
        providerFilter: 'all',
        gameFilter: 'all',
        currencyFilter: 'all',
        amountFilter: { min: '', max: '', type: 'all' },
        sportsResultFilter: 'all',
        searchTerm: ''
      };
    case 'LOAD_FROM_URL':
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const initialFilterState = {
  dateRange: 'last7',
  customStartDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
  customEndDate: new Date(),
  activeTab: 'sport',
  providerFilter: 'all',
  sortOrder: 'newest',
  sportsResultFilter: 'all',
  amountFilter: { min: '', max: '', type: 'all' },
  gameFilter: 'all',
  currencyFilter: 'all',
  searchTerm: ''
};

const BetHistory = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const [filters, dispatchFilter] = useReducer(filterReducer, initialFilterState);
  const { dateRange, customStartDate, customEndDate, activeTab, providerFilter, sortOrder, sportsResultFilter, amountFilter, gameFilter, currencyFilter, searchTerm } = filters;
  
  // Deep links (e.g. from the transactions settlement audit) point at a single
  // bet and carry the window it lives in, because the sports tab otherwise
  // only ever loads the last 60 days and would miss an older placement.
  const [focusBetId, setFocusBetId] = useState(() =>
    new URLSearchParams(window.location.search).get('bet')
  );
  const [windowOverride] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const from = params.get('from');
    const to = params.get('to');
    if (!from || !to) return null;
    const start = new Date(from);
    const end = new Date(to);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
    return { start, end };
  });

  const [selectedUsers, setSelectedUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [openBets, setOpenBets] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [platformFilter, setPlatformFilter] = useState('all'); // Platform filter (deprecated - using activeTab instead)
  const [showOpenSportsBets, setShowOpenSportsBets] = useState(false); // Filter for open/pending sports bets (deprecated)
  const [filtersExpanded, setFiltersExpanded] = useState(false); // Mobile filter panel state
  const [autoLoadAttempts, setAutoLoadAttempts] = useState(0); // Track auto-load attempts to prevent infinite loops
  const [deepSearchActive, setDeepSearchActive] = useState(false); // Track if deep search is active
  const [deepSearchProgress, setDeepSearchProgress] = useState(0); // Track deep search progress (0-100)
  const [userTree, setUserTree] = useState(null);
  const [loadingUserTree, setLoadingUserTree] = useState(true);
  const [nextTokens, setNextTokens] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [initialUserSet, setInitialUserSet] = useState(false); // Track if initial user has been set
  const [visibleRoundsCount, setVisibleRoundsCount] = useState(50); // Number of rounds to display at once
  const [loadingMoreRounds, setLoadingMoreRounds] = useState(false); // Loading state for frontend pagination
  const [selfUserType, setSelfUserType] = useState(null); // Current user's type
  const [refreshCooldown, setRefreshCooldown] = useState(0); // Cooldown timer in seconds
  const cooldownTimerRef = useRef(null); // Ref for cooldown timer
  const deepSearchCancelledForSortRef = useRef(null); // Tracks cancelled sort-triggered deep search to prevent re-trigger
  const pageSize = 200; // Increased page size for more transactions per load
  const ROUNDS_PER_PAGE = 50; // Number of rounds to show at once on frontend
  const REFRESH_COOLDOWN_SECONDS = 30; // Cooldown duration for restricted user types

  // Get all players (only leaf nodes that are actual players)
  const effectivePlayers = useMemo(() => {
    const playerSet = new Set();
    
    selectedUsers.forEach(user => {
      // Get all players under this node
      const nodePlayers = getAllPlayersUnderNode(user);
      
      // For each player, just add their username without currency suffixes
      // The backend will handle matching transactions for all currency variants
      nodePlayers.forEach(player => {
        playerSet.add(player.username);
      });
    });
    
    // Convert Set to Array to remove duplicates
    const players = Array.from(playerSet);
    console.log('Effective players for query:', players);
    return players;
  }, [selectedUsers]);

  // Load filters from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    
    // Only load once on mount
    if (filtersLoaded) return;
    
    // Load filter state from URL
    const urlTab = params.get('tab');
    const urlCurrency = params.get('currency');
    const urlProvider = params.get('provider');
    const urlGame = params.get('game');
    const urlAmountMin = params.get('amount_min');
    const urlAmountMax = params.get('amount_max');
    const urlAmountType = params.get('amount_type');
    const urlSportsFilter = params.get('sports_filter');
    const urlDateRange = params.get('date_range');
    const urlSortOrder = params.get('sort');
    const urlSearch = params.get('search');
    
    const urlFilters = {};
    if (urlTab && (urlTab === 'sport' || urlTab === 'casino')) urlFilters.activeTab = urlTab;
    if (urlCurrency) urlFilters.currencyFilter = urlCurrency;
    if (urlProvider) urlFilters.providerFilter = urlProvider;
    if (urlGame) urlFilters.gameFilter = urlGame;
    if (urlSearch) urlFilters.searchTerm = urlSearch;
    if (urlAmountMin || urlAmountMax || urlAmountType) {
      urlFilters.amountFilter = {
        min: urlAmountMin || '',
        max: urlAmountMax || '',
        type: urlAmountType || 'all'
      };
    }
    if (urlSportsFilter) urlFilters.sportsResultFilter = urlSportsFilter;
    if (urlDateRange) urlFilters.dateRange = urlDateRange;
    if (urlSortOrder) urlFilters.sortOrder = urlSortOrder;
    
    if (Object.keys(urlFilters).length > 0) {
      dispatchFilter({ type: 'LOAD_FROM_URL', payload: urlFilters });
    }
    
    setFiltersLoaded(true);
  }, [location.search, filtersLoaded]);

  // Save filters to URL when they change
  useEffect(() => {
    // Don't update URL until initial load is complete
    if (!filtersLoaded) return;
    
    const params = new URLSearchParams();
    
    // Add active filters to URL
    if (activeTab !== 'sport') params.set('tab', activeTab);
    if (currencyFilter !== 'all') params.set('currency', currencyFilter);
    if (providerFilter !== 'all') params.set('provider', providerFilter);
    if (gameFilter !== 'all') params.set('game', gameFilter);
    if (amountFilter.min) params.set('amount_min', amountFilter.min);
    if (amountFilter.max) params.set('amount_max', amountFilter.max);
    if (amountFilter.type !== 'all') params.set('amount_type', amountFilter.type);
    if (sportsResultFilter !== 'all') params.set('sports_filter', sportsResultFilter);
    if (dateRange !== 'last7') params.set('date_range', dateRange);
    if (sortOrder !== 'newest') params.set('sort', sortOrder);
    if (searchTerm.trim()) params.set('search', searchTerm.trim());
    
    // Keep deep-link params alive across filter changes; they are owned by the
    // incoming link rather than by the filter panel.
    const incoming = new URLSearchParams(location.search);
    ['from', 'to'].forEach((key) => {
      const value = incoming.get(key);
      if (value) params.set(key, value);
    });
    if (focusBetId) params.set('bet', focusBetId);
    
    // Update URL without reloading page
    const newSearch = params.toString();
    const currentSearch = location.search.substring(1);
    
    if (newSearch !== currentSearch) {
      navigate(`${location.pathname}?${newSearch}`, { replace: true });
    }
  }, [activeTab, currencyFilter, providerFilter, gameFilter, amountFilter, sportsResultFilter, dateRange, sortOrder, searchTerm, focusBetId, filtersLoaded, navigate, location.pathname, location.search]);

  // Load user tree on mount (use cache if available)
  useEffect(() => {
    const loadUserTree = async () => {
      try {
        // Try to get cached version immediately (non-blocking)
        const cachedTree = getCachedTree();
        if (cachedTree) {
          setUserTree(cachedTree);
          setLoadingUserTree(false);
        } else {
          setLoadingUserTree(true);
        }
        
        // Then refresh in the background
        fetchUserTree(false, { staleWhileRevalidate: true }).then((freshTree) => {
          if (freshTree) {
            setUserTree(freshTree);
          }
        }).catch(error => {
          console.error('Error refreshing user tree:', error);
        }).finally(() => {
          setLoadingUserTree(false);
        });
      } catch (error) {
        console.error('Error loading user tree:', error);
        setLoadingUserTree(false);
      }
    };

    loadUserTree();
  }, []);

  // Fetch self user type on mount for refresh cooldown logic
  useEffect(() => {
    const fetchSelfType = async () => {
      try {
        const userType = await getSelfUserType();
        setSelfUserType(userType);
        console.log('Self user type for cooldown:', userType);
      } catch (error) {
        console.error('Error fetching self user type:', error);
      }
    };

    fetchSelfType();
  }, []);

  // Cleanup cooldown timer on unmount
  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
      }
    };
  }, []);

  // Check if current user type requires refresh cooldown
  const requiresCooldown = useMemo(() => {
    const restrictedTypes = ['agent', 'super-agent', 'city'];
    return restrictedTypes.includes(selfUserType?.toLowerCase());
  }, [selfUserType]);

  // Only the user types that can reach the Limits page get the shortcut into it
  const canSetLimits = useMemo(() => canManageLimits(selfUserType), [selfUserType]);

  // Start cooldown timer
  const startCooldown = useCallback(() => {
    if (!requiresCooldown) return;
    
    setRefreshCooldown(REFRESH_COOLDOWN_SECONDS);
    
    // Clear any existing timer
    if (cooldownTimerRef.current) {
      clearInterval(cooldownTimerRef.current);
    }
    
    // Start countdown timer
    cooldownTimerRef.current = setInterval(() => {
      setRefreshCooldown(prev => {
        if (prev <= 1) {
          clearInterval(cooldownTimerRef.current);
          cooldownTimerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [requiresCooldown]);

  // Handle selectedUser from navigation (e.g., from UserDeepView)
  useEffect(() => {
    if (location.state?.selectedUser && userTree && !initialUserSet) {
      const selectedUser = location.state.selectedUser;
      console.log('Setting selected user from navigation:', selectedUser);
      
      // Find the user in the tree
      const findUserInTree = (node, targetUsername) => {
        if (node.username === targetUsername) {
          return node;
        }
        if (node.children) {
          for (const child of node.children) {
            const found = findUserInTree(child, targetUsername);
            if (found) return found;
          }
        }
        return null;
      };
      
      const userNode = findUserInTree(userTree, selectedUser.username);
      if (userNode) {
        setSelectedUsers([userNode]);
        setInitialUserSet(true);
        
        // Clear the state to prevent re-selection on subsequent renders
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, userTree, initialUserSet]);

  const fetchTransactions = useCallback(async (isLoadMore = false) => {
    if (effectivePlayers.length === 0) {
      setTransactions([]);
      setOpenBets([]);
      setHasMore(false);
      return;
    }

    // Check if too many players are selected - different limits for sport vs casino
    const MAX_PLAYERS = activeTab === 'sport' ? 1000 : 200;
    if (effectivePlayers.length > MAX_PLAYERS) {
      setTransactions([]);
      setOpenBets([]);
      setHasMore(false);
      setError(t('betHistory.tooManyPlayers', 
        `Too many players selected ({{count}}). Maximum allowed is {{max}} for ${activeTab}. Please select individual players or an agent with fewer players underneath.`,
        { count: effectivePlayers.length, max: MAX_PLAYERS }
      ));
      setLoading(false);
      return;
    }

    if (isLoadMore && activeTab === 'casino') {
      setLoadingMore(true);
    } else {
      setLoading(true);
      // Reset pagination when not loading more
      setNextTokens(null);
      setTotalCount(0);
      setTransactions([]); // Clear existing transactions when doing a fresh search
      setOpenBets([]); // Clear existing open bets
      console.log('Reset pagination and cleared transactions for fresh query');
    }
    setError(null);

    try {
      // Batch players if more than 10
      const BATCH_SIZE = 10;
      const shouldBatch = effectivePlayers.length > BATCH_SIZE;
      let playerBatches = [];
      
      if (shouldBatch) {
        // Split players into batches of 10
        for (let i = 0; i < effectivePlayers.length; i += BATCH_SIZE) {
          playerBatches.push(effectivePlayers.slice(i, i + BATCH_SIZE));
        }
        console.log(`Batching ${effectivePlayers.length} players into ${playerBatches.length} batches of up to ${BATCH_SIZE} players each`);
      } else {
        // Single batch with all players
        playerBatches = [effectivePlayers];
      }
      // Calculate date range based on tab
      let endDate = endOfDay(new Date());
      let startDate;
      
      if (windowOverride) {
        // A deep link asked for an explicit window — honour it on both tabs.
        startDate = windowOverride.start;
        endDate = windowOverride.end;
      } else if (activeTab === 'sport') {
        // For sports, always get 2 months of data
        startDate = startOfDay(subDays(endDate, 60)); // 2 months back
      } else {
        // For casino, use the date range picker
        switch (dateRange) {
          case 'today':
            startDate = startOfDay(new Date());
            break;
          case 'last7':
            startDate = startOfDay(subDays(endDate, 7));
            break;
          case 'last30':
            startDate = startOfDay(subDays(endDate, 30));
            break;
          case 'thisMonth':
            startDate = startOfMonth(endDate);
            break;
          case 'lastMonth':
            const lastMonth = subDays(startOfMonth(endDate), 1);
            startDate = startOfMonth(lastMonth);
            endDate = endOfMonth(lastMonth);
            break;
          case 'custom':
            // Use the custom dates selected by the user
            startDate = customStartDate;
            endDate = customEndDate;
            break;
          case 'all':
            // Set a reasonable date range for all transactions (last 2 years)
            startDate = startOfDay(subDays(endDate, 730)); // 2 years back
            endDate = endOfDay(new Date());
            break;
          default:
            startDate = startOfDay(subDays(endDate, 7));
        }
      }

      // Debug logging
      console.log('=== Date Range Debug ===');
      console.log('Date range selected:', dateRange);
      console.log('Local timezone:', Intl.DateTimeFormat().resolvedOptions().timeZone);
      console.log('Timezone offset (minutes):', new Date().getTimezoneOffset());
      console.log('Current local time:', new Date().toString());
      console.log('Start date (local):', startDate.toString());
      console.log('End date (local):', endDate.toString());
      console.log('Start date (UTC/ISO):', formatDateForAPI(startDate));
      console.log('End date (UTC/ISO):', formatDateForAPI(endDate));
      console.log('Start timestamp (ms):', startDate.getTime());
      console.log('End timestamp (ms):', endDate.getTime());
      console.log('Days covered:', Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)));
      console.log('Hours covered:', Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60)));
      console.log('======================');

      console.log('Fetching transactions for players:', effectivePlayers);
      console.log('Date range:', formatDateForAPI(startDate), 'to', formatDateForAPI(endDate));
      console.log('Is load more:', isLoadMore);
      console.log('Current nextTokens:', nextTokens);
      console.log('Sending nextTokens:', isLoadMore ? nextTokens : undefined);

      // Get the current session token
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      // Prepare base API request parameters (without playerIds)
      const baseRequestParams = {
        startDate: formatDateForAPI(startDate),
        endDate: formatDateForAPI(endDate),
        platform: activeTab === 'sport' ? 'sport' : activeTab === 'casino' ? 'casino' : undefined
      };

      // For sports, get all data at once (no pagination)
      // For casino, use pagination
      if (activeTab === 'sport') {
        baseRequestParams.limit = 10000; // Large limit to get all sports data
      } else {
        baseRequestParams.limit = pageSize;
        if (isLoadMore && nextTokens) {
          baseRequestParams.nextTokens = nextTokens;
        }
      }

      let allNewTransactions = [];
      let combinedHasMore = false;
      let combinedNextTokens = null;
      let combinedTotalCount = 0;

      if (shouldBatch && !isLoadMore) {
        // Execute parallel batch requests (only for initial load, not for pagination)
        console.log(`Executing ${playerBatches.length} parallel API calls for player batches`);
        
        const batchPromises = playerBatches.map(async (batch, index) => {
          const batchParams = {
            ...baseRequestParams,
            playerIds: batch
          };
          
          console.log(`Batch ${index + 1}: Fetching for ${batch.length} players`);
          const batchResponse = await apiPost(
            'analytics',
            '/gametransactions',
            batchParams,
            token
          );
          
          return batchResponse;
        });

        // Wait for all batches to complete (with error handling for partial failures)
        const batchResponses = await Promise.allSettled(batchPromises);
        
        // Combine results from all batches
        let successfulBatches = 0;
        let failedBatches = 0;
        
        batchResponses.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            const response = result.value;
            if (response.success && response.data) {
              console.log(`Batch ${index + 1}: Retrieved ${response.data.length} transactions`);
              allNewTransactions = allNewTransactions.concat(response.data);
              successfulBatches++;
              
              // For pagination info, we'll use the first batch's pagination
              // Note: Batching limits pagination effectiveness, but we keep it enabled
              // to support deep search functionality
              if (index === 0) {
                combinedHasMore = response.hasMore || false; // Keep pagination info from first batch
                combinedNextTokens = response.nextTokens || null;
                combinedTotalCount += response.totalCount || response.data.length;
              } else {
                combinedTotalCount += response.totalCount || response.data.length;
              }
            } else {
              console.error(`Batch ${index + 1}: Failed with error:`, response.error);
              failedBatches++;
            }
          } else {
            console.error(`Batch ${index + 1}: Failed with error:`, result.reason);
            failedBatches++;
          }
        });
        
        if (failedBatches > 0 && successfulBatches === 0) {
          throw new Error(`All ${failedBatches} batch requests failed`);
        } else if (failedBatches > 0) {
          console.warn(`${failedBatches} out of ${playerBatches.length} batches failed, but ${successfulBatches} succeeded`);
        }
        
        console.log(`Total transactions from all batches: ${allNewTransactions.length}`);
      } else {
        // Single request (either single batch or load more)
        const requestParams = {
          ...baseRequestParams,
          playerIds: effectivePlayers
        };
        
        const response = await apiPost(
          'analytics',
          '/gametransactions',
          requestParams,
          token
        );

      console.log('Game transactions API response:', response);
      console.log('Response nextTokens:', response.nextTokens);
      console.log('Response hasMore:', response.hasMore);
      console.log('Response totalCount:', response.totalCount);
      
      // Enhanced diagnostics for debugging date range issues
      if (response.data && response.data.length > 0) {
        const timestamps = response.data.map(tx => getTransactionTimestamp(tx));
        const oldestTx = response.data.find(tx => getTransactionTimestamp(tx) === Math.min(...timestamps));
        const newestTx = response.data.find(tx => getTransactionTimestamp(tx) === Math.max(...timestamps));
        
        console.log('=== BACKEND RESPONSE ANALYSIS ===');
        console.log('Transactions returned in this batch:', response.data.length);
        console.log('Oldest transaction in batch:', {
          id: oldestTx?.transactionId,
          timestamp: new Date(getTransactionTimestamp(oldestTx)).toISOString(),
          localTime: new Date(getTransactionTimestamp(oldestTx)).toString()
        });
        console.log('Newest transaction in batch:', {
          id: newestTx?.transactionId,
          timestamp: new Date(getTransactionTimestamp(newestTx)).toISOString(),
          localTime: new Date(getTransactionTimestamp(newestTx)).toString()
        });
        console.log('Time span in this batch (hours):', 
          Math.round((Math.max(...timestamps) - Math.min(...timestamps)) / (1000 * 60 * 60))
        );
        console.log('================================');
      }

        if (response.success && response.data) {
          allNewTransactions = response.data;
          combinedHasMore = response.hasMore || false;
          combinedNextTokens = response.nextTokens || null;
          combinedTotalCount = response.totalCount || 0;
        }
      }

      if (allNewTransactions.length > 0) {
        const newTransactions = allNewTransactions;
        
        // Debug: Log some sample transactions to check their structure
        console.log('Sample transactions:', newTransactions.slice(0, 5));
        
        // Debug: Check for gameCode fields in transactions
        const transactionsWithGameCode = newTransactions.filter(tx => 
          tx.context?.gameCode || tx.metadata?.gameCode
        );
        console.log(`Found ${transactionsWithGameCode.length} transactions with gameCode field out of ${newTransactions.length} total`);
        if (transactionsWithGameCode.length > 0) {
          console.log('Sample transactions with gameCode:', transactionsWithGameCode.slice(0, 3));
        }
        
        // Debug: Look for sports bets specifically
        const sportsBets = newTransactions.filter(tx => 
          tx.platform?.toLowerCase() === 'sport' || 
          tx.platform?.toLowerCase() === 'sports' ||
          tx.context?.product?.toLowerCase() === 'sportsbook'
        );
        console.log(`Found ${sportsBets.length} sports bets out of ${newTransactions.length} total`);
        if (sportsBets.length > 0) {
          console.log('Sample sports bets:', sportsBets.slice(0, 3));
        }
        
        // Ensure transactions are sorted by timestamp (newest first)
        // The backend should already sort them, but let's make sure
        const sortedTransactions = [...newTransactions].sort((a, b) => {
          const timestampA = getTransactionTimestamp(a);
          const timestampB = getTransactionTimestamp(b);
          return timestampB - timestampA; // Newest first
        });
        
        // Update transactions state
        if (isLoadMore) {
          setTransactions(prev => [...prev, ...sortedTransactions]);
        } else {
          setTransactions(sortedTransactions);
        }

        // For sports bets, we need to check if there's a corresponding settle transaction
        // We need to check ALL transactions, not just new ones
        const allTransactionsToCheck = isLoadMore ? [...transactions, ...newTransactions] : newTransactions;
        
        // First, get all bet IDs from settle transactions
        // For original provider: deposits with reason "settle" and betId
        // For TurboStars: deposits with -settle- in transactionId or reason includes "settlement"
        const settledBetIds = new Set();
        const settledTurboStarsIds = new Set();
        
        allTransactionsToCheck.forEach(tx => {
          // Skip internal BetConstruct tracker rows
          if (isBetConstructTrackerRow(tx)) return;
          
          // Original provider settle transactions
          if (tx.type?.toLowerCase() === 'deposit' && 
              tx.context?.reason?.toLowerCase() === 'settle' && 
              tx.context?.betId) {
            settledBetIds.add(tx.context.betId);
          }
          
          // BetConstruct result transactions also close the bet
          if (isBetConstructResult(tx) && tx.context?.betId) {
            settledBetIds.add(tx.context.betId);
          }
          
          // BetConstruct rollback also closes the bet (stake refunded)
          if (isBetConstructRollback(tx) && tx.context?.betId) {
            settledBetIds.add(tx.context.betId);
          }
          
          // TurboStars settle transactions
          if (tx.type?.toLowerCase() === 'deposit' && isTurboStarsTransaction(tx)) {
            // Get the original bet transaction ID from parentId or additionalContext
            const originalBetId = tx.context?.parentId || 
                                  tx.context?.additionalContext?.originalBetTransactionId;
            if (originalBetId) {
              settledTurboStarsIds.add(originalBetId);
            }
          }
        });
        
        // Also check rollback transactions for TurboStars
        const rolledBackTurboStarsIds = new Set();
        allTransactionsToCheck.forEach(tx => {
          if (tx.type?.toLowerCase() === 'rollback' || tx.transactionId?.includes('-rollback-')) {
            const originalBetId = tx.context?.parentId || 
                                  tx.context?.additionalContext?.originalBetTransactionId;
            if (originalBetId) {
              rolledBackTurboStarsIds.add(originalBetId);
            }
          }
        });
        
        console.log('Settled bet IDs found:', Array.from(settledBetIds));
        console.log('Settled TurboStars IDs found:', Array.from(settledTurboStarsIds));
        console.log('Rolled back TurboStars IDs found:', Array.from(rolledBackTurboStarsIds));
        
        // Now filter for open bets from ALL transactions
        const allOpenSportsBets = allTransactionsToCheck.filter(tx => {
          // Never surface tracker rows as open bets
          if (isBetConstructTrackerRow(tx)) return false;
          
          const isPlatformSport = tx.platform?.toLowerCase() === 'sport' || 
                                 tx.platform?.toLowerCase() === 'sports' ||
                                 tx.context?.product?.toLowerCase() === 'sportsbook';
          
          if (!isPlatformSport) {
            return false;
          }
          
          // Check if this is a TurboStars bet
          if (isTurboStarsTransaction(tx)) {
            // TurboStars bets are withdrawals not containing -settle- or -rollback-
            const isWithdrawalBet = tx.type?.toLowerCase() === 'withdrawal' && 
                                    !tx.transactionId?.includes('-settle-') &&
                                    !tx.transactionId?.includes('-rollback-');
            
            if (!isWithdrawalBet) {
              return false;
            }
            
            // Check if bet was rolled back
            if (tx.rolledBack === 'true' || tx.rolledBack === true || 
                rolledBackTurboStarsIds.has(tx.transactionId)) {
              return false;
            }
            
            // Check if bet is settled via settled field or settle transaction
            if (tx.settled === 'true' || tx.settled === true || 
                settledTurboStarsIds.has(tx.transactionId)) {
              return false;
            }
            
            // Debug logging
            console.log('TurboStars bet open check:', {
              transactionId: tx.transactionId,
              settled: tx.settled,
              rolledBack: tx.rolledBack,
              hasSettleTx: settledTurboStarsIds.has(tx.transactionId),
              hasRollbackTx: rolledBackTurboStarsIds.has(tx.transactionId),
              isOpen: true,
              timestamp: tx.timestampISO
            });
            
            return true;
          }
          
          // Original provider: Sports bets are withdrawals with context.reason = "bet"
          // BetConstruct: withdrawals with reason "BetConstruct bet" are bet placements
          const isWithdrawalBet = tx.type?.toLowerCase() === 'withdrawal' &&
                                  (tx.context?.reason?.toLowerCase() === 'bet' ||
                                   isBetConstructBet(tx));
          
          if (!isWithdrawalBet) {
            return false;
          }
          
          // For sports bets, "completed" status just means the bet was placed successfully
          // The bet is open if there's no corresponding settle transaction
          const betId = tx.context?.betId;
          const isOpen = betId && !settledBetIds.has(betId);
          
          // Debug logging for sports bets
          if (isPlatformSport && isWithdrawalBet) {
            console.log('Sports bet open check:', {
              transactionId: tx.transactionId,
              betId: betId,
              hasSettlement: betId ? settledBetIds.has(betId) : 'no betId',
              isOpen,
              timestamp: tx.timestampISO
            });
          }
          
          return isOpen;
        });
        
        console.log(`Found ${allOpenSportsBets.length} open sports bets`);

        // Update open bets state - always set the full list
        // Remove duplicates by using a Map with transactionId as key
        const openBetsMap = new Map();
        allOpenSportsBets.forEach(bet => {
          openBetsMap.set(bet.transactionId, bet);
        });
        const uniqueOpenBets = Array.from(openBetsMap.values());
        
        // Sort by timestamp (newest first)
        uniqueOpenBets.sort((a, b) => {
          const timestampA = getTransactionTimestamp(a);
          const timestampB = getTransactionTimestamp(b);
          return timestampB - timestampA;
        });
        
        setOpenBets(uniqueOpenBets);

        // Update pagination state with combined values
        setHasMore(combinedHasMore);
        setNextTokens(combinedNextTokens);
        setTotalCount(combinedTotalCount);
        console.log('Updated pagination state:', {
          hasMore: combinedHasMore,
          nextTokens: combinedNextTokens,
          totalCount: combinedTotalCount,
          wasBatched: shouldBatch && !isLoadMore
        });
      } else {
        setError(t('betHistory.fetchError', 'Failed to fetch transactions'));
        setTransactions([]);
        setOpenBets([]);
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setError(t('betHistory.fetchError', 'Failed to fetch transactions'));
      setTransactions([]);
      setOpenBets([]);
      setHasMore(false);
    } finally {
      if (isLoadMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  }, [effectivePlayers, dateRange, customStartDate, customEndDate, activeTab, windowOverride, transactions, openBets, pageSize, nextTokens]);

  // Fetch transactions when dependencies change
  useEffect(() => {
    if (effectivePlayers.length > 0) {
      fetchTransactions(false);
    }
  }, [effectivePlayers, dateRange, customStartDate, customEndDate, activeTab, windowOverride]); // Removed fetchTransactions from dependencies to avoid infinite loops

  const handleRefresh = async () => {
    await fetchTransactions(false);
    // Start cooldown after refresh for restricted user types
    startCooldown();
  };

  const handleLoadMore = async () => {
    console.log('handleLoadMore called with nextTokens:', nextTokens);
    await fetchTransactions(true);
  };

  // Deep search handler - triggers deep search mode
  const handleDeepSearch = useCallback(() => {
    if (deepSearchActive) return; // Prevent multiple concurrent deep searches
    
    console.log('=== DEEP SEARCH STARTED ===');
    console.log('Amount filter:', amountFilter);
    console.log('Players selected:', effectivePlayers.length);
    console.log('Batching enabled:', effectivePlayers.length > 10);
    console.log('Current transactions loaded:', transactions.length);
    console.log('Has more data:', hasMore);
    console.log('========================');
    
    setDeepSearchActive(true);
    setDeepSearchProgress(0);
    setAutoLoadAttempts(0); // Reset auto-load attempts for deep search
  }, [deepSearchActive, amountFilter, effectivePlayers.length, transactions.length, hasMore]);

  // Cancel deep search
  const handleCancelDeepSearch = useCallback(() => {
    console.log('Deep search cancelled by user');
    deepSearchCancelledForSortRef.current = sortOrder;
    setDeepSearchActive(false);
    setDeepSearchProgress(0);
    setAutoLoadAttempts(0);
  }, [sortOrder]);

  // Extract unique providers from transactions
  const availableProviders = useMemo(() => {
    const providers = new Set();
    transactions.forEach(tx => {
      // Ignore internal BetConstruct tracker rows
      if (isBetConstructTrackerRow(tx)) return;
      const provider = tx.context?.additionalContext?.provider || 
                      tx.context?.provider || 
                      tx.metadata?.provider;
      if (provider) {
        const normalized = normalizeProviderName(provider);
        if (normalized) {
          providers.add(normalized);
        }
      }
    });
    return Array.from(providers).sort();
  }, [transactions]);

  // Helper to check if a transaction matches the provider filter
  const transactionMatchesProvider = (tx, filterProvider) => {
    if (filterProvider === 'all') return true;
    const provider = tx.context?.additionalContext?.provider || 
                     tx.context?.provider || 
                     tx.metadata?.provider;
    const normalized = normalizeProviderName(provider);
    return normalized === filterProvider;
  };

  // Extract unique currencies from transactions
  const availableCurrencies = useMemo(() => {
    const currencies = new Set();
    transactions.forEach(tx => {
      if (tx.currency) {
        currencies.add(tx.currency);
      }
    });
    return Array.from(currencies).sort();
  }, [transactions]);

  // Extract unique games from transactions
  const availableGames = useMemo(() => {
    const gamesMap = new Map();
    transactions.forEach(tx => {
      // Try to get gameCode first, then fallback to other fields
      const gameCode = tx.context?.gameCode || tx.metadata?.gameCode;
      const gameDetails = tx.metadata?.gameDetails;
      const gameId = tx.context?.gameId;
      const rawGameName = gameCode || gameDetails?.name || gameId;
      
      if (rawGameName && !gamesMap.has(rawGameName)) {
        // Use the same formatGameName function that includes mapping utility
        const formattedName = getGameNameWithFallback(rawGameName, null) || 
          rawGameName
            .replace(/^vs\d+/, '') // Remove "vs20" prefix
            .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space before capitals
            .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2') // Handle consecutive capitals
            .replace(/_/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
          
        gamesMap.set(rawGameName, {
          id: rawGameName,
          name: formattedName || rawGameName,
          platform: tx.platform?.toLowerCase() || 'unknown',
          gameCode: gameCode // Store the original gameCode for reference
        });
      }
    });
    return Array.from(gamesMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [transactions]);

  const displayedRounds = useMemo(() => {
    // Group transactions into rounds with pre-calculated stats
    const rounds = groupTransactionsByRound(transactions);
    
    // A deep link to one bet bypasses every other filter, so an inherited
    // filter can never hide the bet the link was pointing at.
    if (focusBetId) {
      return rounds.filter(round =>
        round.roundId === focusBetId ||
        round.transactions.some(tx =>
          tx.transactionId === focusBetId || tx.context?.betId === focusBetId
        )
      );
    }
    
    // Single-pass filtering with combined predicates
    const filteredRounds = rounds.filter(round => {
      const { stats } = round;
      
      // Sports result filter
      if (activeTab === 'sport' && sportsResultFilter !== 'all') {
        // Skip rolled back bets from filtered views
        if (stats.hasRollback) return false;
        
        if (sportsResultFilter === 'open') {
          if (!stats.hasBet || stats.hasSettlement) return false;
        } else if (sportsResultFilter === 'wins') {
          if (!stats.hasSettlement) return false;
          if (stats.settleResult !== 'Win' && stats.settleResult !== 'Cashout' && stats.netResult <= 0) return false;
        } else if (sportsResultFilter === 'losses') {
          if (!stats.hasSettlement) return false;
          if (stats.settleResult !== 'Lose' && stats.netResult >= 0) return false;
        }
      }
      
      // Currency filter
      if (currencyFilter !== 'all' && stats.currency !== currencyFilter) {
        return false;
      }
      
      // Provider filter
      if (providerFilter !== 'all' && stats.provider !== providerFilter) {
        return false;
      }
      
      // Game filter
      if (gameFilter !== 'all' && stats.rawGameName !== gameFilter) {
        return false;
      }
      
      // Search term filter (matches description or game name)
      if (searchTerm.trim()) {
        const query = searchTerm.trim().toLowerCase();
        const matchesGameName = stats.resolvedGameName?.toLowerCase().includes(query);
        const matchesRawGameName = stats.rawGameName?.toLowerCase().includes(query);
        const matchesDescription = stats.description?.toLowerCase().includes(query);
        if (!matchesGameName && !matchesRawGameName && !matchesDescription) {
          return false;
        }
      }
      
      // Amount filter with 20% tolerance
      if (amountFilter.min !== '' || amountFilter.max !== '' || amountFilter.type !== 'all') {
        // Filter by result type
        if (amountFilter.type === 'win' && stats.netResult <= 0) return false;
        if (amountFilter.type === 'loss' && stats.netResult >= 0) return false;
        
        // Filter by amount range with 20% tolerance
        const absNetResult = Math.abs(stats.netResult);
        const minAmount = amountFilter.min !== '' ? parseFloat(amountFilter.min) : 0;
        const maxAmount = amountFilter.max !== '' ? parseFloat(amountFilter.max) : Infinity;
        
        if (minAmount > 0) {
          const minWithTolerance = minAmount * 0.8;
          if (absNetResult < minWithTolerance) return false;
        }
        
        if (maxAmount < Infinity) {
          const maxWithTolerance = maxAmount * 1.2;
          if (absNetResult > maxWithTolerance) return false;
        }
      }
      
      return true;
    });
    
    // Sort using pre-calculated values
    filteredRounds.sort((a, b) => {
      if (sortOrder === 'highestWin') {
        return b.stats.netResult - a.stats.netResult;
      } else if (sortOrder === 'lowestWin') {
        return a.stats.netResult - b.stats.netResult;
      } else if (sortOrder === 'oldest') {
        return a.latestTimestamp - b.latestTimestamp;
      } else {
        return b.latestTimestamp - a.latestTimestamp; // newest first (default)
      }
    });
    
    return filteredRounds;
  }, [activeTab, transactions, providerFilter, sortOrder, gameFilter, amountFilter, sportsResultFilter, currencyFilter, searchTerm, focusBetId]);

  // Auto-load more data when filters are active and results are limited
  useEffect(() => {
    // Don't auto-load if deep search is active
    if (deepSearchActive) return;

    const hasActiveFilters = 
      providerFilter !== 'all' || 
      gameFilter !== 'all' || 
      currencyFilter !== 'all' ||
      amountFilter.type !== 'all' || 
      amountFilter.min !== '' || 
      amountFilter.max !== '' ||
      searchTerm.trim() !== '';

    // Check if amount filter is active (for deep search)
    const hasAmountFilter = 
      amountFilter.min !== '' || 
      amountFilter.max !== '' || 
      amountFilter.type !== 'all';

    // Sports doesn't need auto-loading since we get all data at once
    // Only casino might need auto-loading when filters are active
    // BUT: Don't auto-load when amount filter is active - let user trigger deep search instead
    const needsAutoLoad = activeTab === 'casino' && hasActiveFilters && !hasAmountFilter;
    
    // Auto-loading only for casino with filters (excluding amount filter)
    const minResultsThreshold = needsAutoLoad ? 10 : 0;
    const maxAutoLoadAttempts = needsAutoLoad ? 25 : 0;
    
    // Only auto-load if:
    // 1. Casino tab with filters active
    // 2. We have fewer results than threshold
    // 3. There's more data to load
    // 4. Not currently loading
    // 5. Haven't exceeded max auto-load attempts
    if (
      needsAutoLoad &&
      displayedRounds.length < minResultsThreshold &&
      hasMore &&
      !loading &&
      !loadingMore &&
      autoLoadAttempts < maxAutoLoadAttempts
    ) {
      console.log(`Auto-loading more transactions (attempt ${autoLoadAttempts + 1}/${maxAutoLoadAttempts}) - only ${displayedRounds.length} results with active filters`);
      
      // Increment auto-load attempts
      setAutoLoadAttempts(prev => prev + 1);
      
      // Trigger load more
      handleLoadMore();
    }
  }, [displayedRounds.length, providerFilter, gameFilter, amountFilter, activeTab, hasMore, loading, loadingMore, autoLoadAttempts, currencyFilter, searchTerm, deepSearchActive]);

  // Reset auto-load attempts when filters or tab changes
  useEffect(() => {
    setAutoLoadAttempts(0);
  }, [providerFilter, gameFilter, amountFilter, activeTab, currencyFilter]);

  // Deep search effect - continues loading when deep search is active
  useEffect(() => {
    const MAX_DEEP_SEARCH_LOADS = 50;
    
    if (deepSearchActive && !loading && !loadingMore) {
      // Only proceed if we've actually started loading (autoLoadAttempts > 0)
      // This prevents immediate completion on first activation
      if (autoLoadAttempts === 0) {
        // First load - always try to load more
        if (hasMore) {
          console.log('Deep search started - initiating first load');
          setDeepSearchProgress(5);
          setAutoLoadAttempts(1);
          handleLoadMore();
        } else {
          // No data to load at all - shouldn't happen since button only shows when hasMore is true
          console.log('Deep search activated but no data available');
          setDeepSearchActive(false);
          setDeepSearchProgress(0);
        }
        return;
      }
      
      // Continue loading until entire timeframe is loaded or max attempts reached
      // Don't stop when results are found - load the complete time range
      if (hasMore && autoLoadAttempts < MAX_DEEP_SEARCH_LOADS) {
        // No results yet but more data available - continue searching
        // Calculate progress based on transactions loaded vs estimated total
        let progress;
        if (totalCount > 0) {
          // Use actual total count from backend for accurate progress
          progress = Math.min((transactions.length / totalCount) * 100, 95);
        } else {
          // Fallback to attempt-based progress
          progress = Math.min((autoLoadAttempts / MAX_DEEP_SEARCH_LOADS) * 100, 95);
        }
        setDeepSearchProgress(progress);
        
        console.log(`Deep search continuing... (${autoLoadAttempts}/${MAX_DEEP_SEARCH_LOADS} loads) - ${transactions.length}/${totalCount || '?'} transactions loaded`);
        setAutoLoadAttempts(prev => prev + 1);
        handleLoadMore();
      } else {
        // No more data to load or max attempts reached - deep search complete
        const reason = !hasMore 
          ? `complete timeframe loaded (${transactions.length} transactions total)`
          : `max attempts reached (${MAX_DEEP_SEARCH_LOADS} loads)`;
        const resultsFound = displayedRounds.length;
        
        console.log(`Deep search complete - ${resultsFound > 0 ? 'FOUND RESULTS' : 'no matches'}. Reason: ${reason}`);
        console.log(`Deep search summary: Scanned ${transactions.length} transactions across ${autoLoadAttempts} loads, found ${resultsFound} matching results`);
        console.log('Date range of loaded data:', 
          transactions.length > 0 
            ? `${new Date(Math.min(...transactions.map(tx => getTransactionTimestamp(tx)))).toISOString()} to ${new Date(Math.max(...transactions.map(tx => getTransactionTimestamp(tx)))).toISOString()}`
            : 'No data'
        );
        setDeepSearchProgress(100);
        setTimeout(() => {
          setDeepSearchActive(false);
          setDeepSearchProgress(0);
        }, 3000); // Longer timeout to show completion message
      }
    }
  }, [deepSearchActive, displayedRounds.length, hasMore, loading, loadingMore, autoLoadAttempts, handleLoadMore]);

  // Reset deep search when filters or tab changes
  useEffect(() => {
    setDeepSearchActive(false);
    setDeepSearchProgress(0);
  }, [providerFilter, gameFilter, amountFilter, activeTab, currencyFilter, searchTerm, dateRange, customStartDate, customEndDate]);

  // Auto-trigger deep search when sorting by highest/lowest net on casino tab
  // Sorting by net result is meaningless on a partial dataset — we need all data loaded
  useEffect(() => {
    const isSortByNet = sortOrder === 'highestWin' || sortOrder === 'lowestWin';
    
    // Reset cancellation ref when sort order changes away from what was cancelled
    if (deepSearchCancelledForSortRef.current && deepSearchCancelledForSortRef.current !== sortOrder) {
      deepSearchCancelledForSortRef.current = null;
    }
    
    if (
      isSortByNet &&
      activeTab === 'casino' &&
      hasMore &&
      !deepSearchActive &&
      !loading &&
      !loadingMore &&
      transactions.length > 0 &&
      deepSearchCancelledForSortRef.current !== sortOrder
    ) {
      console.log(`Auto-triggering deep search for ${sortOrder} sort — need full dataset`);
      setDeepSearchActive(true);
      setDeepSearchProgress(0);
      setAutoLoadAttempts(0);
    }
  }, [sortOrder, activeTab, hasMore, deepSearchActive, loading, loadingMore, transactions.length]);

  // Keyboard shortcuts - Esc to close filters
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && filtersExpanded) {
        setFiltersExpanded(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [filtersExpanded]);

  // Reset visible rounds count when filters change or new data is loaded
  useEffect(() => {
    setVisibleRoundsCount(ROUNDS_PER_PAGE);
  }, [providerFilter, gameFilter, amountFilter, activeTab, sortOrder, sportsResultFilter, dateRange, customStartDate, customEndDate, transactions.length, currencyFilter, searchTerm]);

  // Check if auto-loading is active
  const isAutoLoading = useMemo(() => {
    const hasActiveFilters = 
      providerFilter !== 'all' || 
      gameFilter !== 'all' || 
      currencyFilter !== 'all' ||
      amountFilter.type !== 'all' || 
      amountFilter.min !== '' || 
      amountFilter.max !== '' ||
      searchTerm.trim() !== '';
    
    return (activeTab === 'casino' && hasActiveFilters && loadingMore && autoLoadAttempts > 0) || deepSearchActive;
  }, [providerFilter, gameFilter, amountFilter, activeTab, loadingMore, autoLoadAttempts, currencyFilter, searchTerm, deepSearchActive]);

  // Get visible rounds for frontend pagination (only show a subset to prevent performance issues)
  const visibleRounds = useMemo(() => {
    return displayedRounds.slice(0, visibleRoundsCount);
  }, [displayedRounds, visibleRoundsCount]);

  // Calculate total net results by currency (only when all data is loaded)
  const totalNetByCurrency = useMemo(() => {
    // Only calculate when all data is loaded and not currently loading
    // For sport: data is always fully loaded (no pagination)
    // For casino: check if hasMore is false AND not loading
    const isFullyLoaded = activeTab === 'sport' || (!hasMore && !loading && !loadingMore);
    
    if (!isFullyLoaded || displayedRounds.length === 0) {
      return null;
    }

    const netByCurrency = new Map();

    displayedRounds.forEach(round => {
      // Use pre-calculated net result and currency
      const currency = round.stats.currency || 'UNKNOWN';
      const netResult = round.stats.netResult;
      
      // Add to currency total
      const currentTotal = netByCurrency.get(currency) || 0;
      netByCurrency.set(currency, currentTotal + netResult);
    });

    // Convert to array and sort by currency name
    return Array.from(netByCurrency.entries())
      .map(([currency, total]) => ({ currency, total }))
      .sort((a, b) => a.currency.localeCompare(b.currency));
  }, [displayedRounds, activeTab, hasMore, loading, loadingMore]);

  // Handle showing more rounds on frontend
  const handleShowMoreRounds = useCallback(() => {
    setLoadingMoreRounds(true);
    // Simulate a small delay for smooth UX
    setTimeout(() => {
      setVisibleRoundsCount(prev => Math.min(prev + ROUNDS_PER_PAGE, displayedRounds.length));
      setLoadingMoreRounds(false);
    }, 100);
  }, [displayedRounds.length]);

  // Detect scroll near bottom for infinite scroll with debounce
  useEffect(() => {
    let scrollTimeout;
    const handleScroll = () => {
      // Clear existing timeout
      if (scrollTimeout) clearTimeout(scrollTimeout);
      
      // Debounce scroll handling
      scrollTimeout = setTimeout(() => {
        // Only trigger if not already loading and there are more rounds to show
        if (loadingMoreRounds || visibleRoundsCount >= displayedRounds.length) return;
        
        const scrollPosition = window.innerHeight + window.scrollY;
        const threshold = document.documentElement.scrollHeight - 500; // 500px from bottom
        
        if (scrollPosition >= threshold) {
          handleShowMoreRounds();
        }
      }, 150); // 150ms debounce
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, [loadingMoreRounds, visibleRoundsCount, displayedRounds.length, handleShowMoreRounds]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Screen reader announcements */}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {loading && <span>{t('common.loading', 'Loading')} {t('betHistory.transactions', 'transactions')}...</span>}
        {deepSearchActive && <span>{t('betHistory.deepSearching', 'Deep searching')}... {Math.round(deepSearchProgress)}%</span>}
        {loadingMore && !deepSearchActive && <span>{t('betHistory.loadingMoreRounds', 'Loading more rounds')}...</span>}
      </div>
      
      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {t('navigation.betHistory')}
              </h1>
              {effectivePlayers.length > 0 && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {effectivePlayers.length} {t('betHistory.playersSelected', 'players selected')}
                </p>
              )}
            </div>
            <button
              onClick={handleRefresh}
              aria-label={t('betHistory.refresh', 'Refresh transactions')}
              disabled={loading || refreshCooldown > 0}
              className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150 shadow-sm"
              title={refreshCooldown > 0 ? t('betHistory.refreshCooldown', 'Please wait {{seconds}}s before refreshing again', { seconds: refreshCooldown }) : ''}
            >
              <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="font-medium hidden sm:inline">
                {refreshCooldown > 0 
                  ? `${t('common.refresh')} (${refreshCooldown}s)` 
                  : t('common.refresh')
                }
              </span>
              {/* Show cooldown on mobile too */}
              {refreshCooldown > 0 && (
                <span className="font-medium sm:hidden text-xs">
                  {refreshCooldown}s
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col gap-4 mb-6">
          {/* User Selector */}
          <div>
            {loadingUserTree ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-3"></div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('betHistory.loadingUsers')}</p>
                </div>
              </div>
            ) : (
              <UserSelector
                selectedUsers={selectedUsers}
                onUsersChange={setSelectedUsers}
                userTree={userTree}
              />
            )}
          </div>

          {/* Date Range - Only show for Casino */}
          {activeTab === 'casino' && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <CalendarIcon className="w-4 h-4" />
                {t('reports.dateRange')}
              </h3>
              <DateRangePicker 
                value={dateRange} 
                onChange={(value) => dispatchFilter({ type: 'SET_DATE_RANGE', payload: value })}
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                onCustomStartChange={(date) => dispatchFilter({ type: 'SET_CUSTOM_DATES', payload: { start: date, end: customEndDate } })}
                onCustomEndChange={(date) => dispatchFilter({ type: 'SET_CUSTOM_DATES', payload: { start: customStartDate, end: date } })}
              />
              
              {/* Date Range Debug Info */}
              {dateRange !== 'custom' && (
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="text-[10px] text-gray-500 dark:text-gray-400 space-y-1">
                    <div className="flex justify-between">
                      <span>{t('betHistory.queryingFrom', 'Querying from')}:</span>
                      <span className="font-mono">
                        {(() => {
                          const endDate = endOfDay(new Date());
                          let startDate;
                          switch (dateRange) {
                            case 'today': startDate = startOfDay(new Date()); break;
                            case 'last7': startDate = startOfDay(subDays(endDate, 7)); break;
                            case 'last30': startDate = startOfDay(subDays(endDate, 30)); break;
                            case 'thisMonth': startDate = startOfMonth(endDate); break;
                            case 'all': startDate = startOfDay(subDays(endDate, 730)); break;
                            default: startDate = startOfDay(subDays(endDate, 7));
                          }
                          return format(startDate, 'MMM d, yyyy HH:mm');
                        })()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t('betHistory.queryingTo', 'Querying to')}:</span>
                      <span className="font-mono">{format(endOfDay(new Date()), 'MMM d, yyyy HH:mm')}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Fixed Date Range Info for Sports */}
          {activeTab === 'sport' && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg shadow-sm border border-blue-200 dark:border-blue-700 p-4">
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                <div>
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                    {t('betHistory.sportsDateRange', 'Sports Data Range')}
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-300">
                    {t('betHistory.showingLast2Months', 'Showing all sports bets from the last 2 months')}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Advanced Filters Toggle - Compact */}
        <div className="mb-4">
          <button
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            aria-label={t('betHistory.advancedFilters', 'Advanced Filters')}
            aria-expanded={filtersExpanded}
            aria-controls="advanced-filters-panel"
            className="flex items-center gap-2 px-3 py-2 text-sm bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
            </svg>
            <span className="text-gray-700 dark:text-gray-300">
              {t('betHistory.advancedFilters', 'Advanced Filters')}
            </span>
            <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ml-auto ${filtersExpanded ? 'rotate-180' : ''}`} aria-hidden="true" />
          </button>

          {/* Advanced Filters Panel */}
          {filtersExpanded && (
            <>
              {/* Mobile overlay */}
              <div 
                className="fixed inset-0 bg-black/50 z-40 sm:hidden"
                onClick={() => setFiltersExpanded(false)}
                aria-hidden="true"
              />
              
              {/* Filter Panel - Mobile bottom sheet / Desktop inline */}
              <div 
                id="advanced-filters-panel" 
                role="region" 
                aria-label={t('betHistory.filterOptions', 'Filter options')} 
                className="
                  fixed bottom-0 left-0 right-0 z-50 
                  sm:relative sm:mt-4 sm:z-0
                  max-h-[80vh] overflow-y-auto
                  p-4 sm:p-4
                  bg-white dark:bg-gray-800 
                  rounded-t-2xl sm:rounded-lg 
                  border-t sm:border border-gray-200 dark:border-gray-700 
                  shadow-2xl sm:shadow-none
                  space-y-4
                  animate-slide-up sm:animate-none
                "
              >
                {/* Mobile drag handle */}
                <div className="sm:hidden flex justify-center -mt-2 mb-2">
                  <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full" />
                </div>
                
                {/* Mobile close button */}
                <div className="sm:hidden flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {t('betHistory.advancedFilters', 'Advanced Filters')}
                  </h3>
                  <button
                    onClick={() => setFiltersExpanded(false)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                    aria-label="Close filters"
                  >
                    <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>
              {/* Search by Description / Game Name */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <MagnifyingGlassIcon className="w-4 h-4" />
                  {t('betHistory.searchFilter', 'Search')}
                </h3>
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder={t('betHistory.searchByDescriptionOrGame', 'Search by game name or description...')}
                    value={searchTerm}
                    onChange={(e) => dispatchFilter({ type: 'SET_SEARCH_TERM', payload: e.target.value })}
                    className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => dispatchFilter({ type: 'SET_SEARCH_TERM', payload: '' })}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                      aria-label="Clear search"
                    >
                      <XMarkIcon className="w-4 h-4 text-gray-400" />
                    </button>
                  )}
                </div>
              </div>

              {/* Amount Filter */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <BanknotesIcon className="w-4 h-4" />
                  {t('betHistory.amountFilter', 'Amount Filter')}
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {t('betHistory.withTolerance', '(±20% tolerance)')}
                  </span>
                </h3>
                <div className="space-y-3">
                  {/* Amount Type */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('betHistory.resultType', 'Result Type')}
                    </label>
                    <div className="flex gap-1 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg">
                      {['all', 'win', 'loss'].map(type => (
                        <button
                          key={type}
                          onClick={() => dispatchFilter({ type: 'UPDATE_AMOUNT_FILTER', payload: { type } })}
                          className={`flex-1 px-3 py-2 rounded-md font-medium text-xs transition-all duration-150 ${
                            amountFilter.type === type
                              ? 'bg-primary-500 text-white shadow-sm'
                              : 'bg-white dark:bg-gray-600 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                          }`}
                        >
                          {type === 'all' ? t('common.all') : 
                           type === 'win' ? t('betHistory.wins', 'Wins') : 
                           t('betHistory.losses', 'Losses')}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Amount Range */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('betHistory.minAmount', 'Min Amount')}
                      </label>
                      <input
                        type="number"
                        placeholder="0"
                        value={amountFilter.min}
                        onChange={(e) => dispatchFilter({ type: 'UPDATE_AMOUNT_FILTER', payload: { min: e.target.value } })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('betHistory.maxAmount', 'Max Amount')}
                      </label>
                      <input
                        type="number"
                        placeholder="∞"
                        value={amountFilter.max}
                        onChange={(e) => dispatchFilter({ type: 'UPDATE_AMOUNT_FILTER', payload: { max: e.target.value } })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                  
                  {/* Clear amount filter */}
                  {(amountFilter.min !== '' || amountFilter.max !== '' || amountFilter.type !== 'all') && (
                    <button
                      onClick={() => dispatchFilter({ type: 'SET_AMOUNT_FILTER', payload: { min: '', max: '', type: 'all' } })}
                      className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      {t('betHistory.clearAmountFilter', 'Clear amount filter')}
                    </button>
                  )}
                </div>
              </div>

              {/* Currency Filter */}
              {availableCurrencies.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                    <BanknotesIcon className="w-4 h-4" />
                    {t('betHistory.currencyFilter', 'Currency Filter')}
                  </h3>
                  <div>
                    <select
                      value={currencyFilter}
                      onChange={(e) => dispatchFilter({ type: 'SET_CURRENCY_FILTER', payload: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      aria-label="Currency filter"
                    >
                      <option value="all">{t('betHistory.allCurrencies', 'All Currencies')} ({availableCurrencies.length})</option>
                      {availableCurrencies.map(currency => (
                        <option key={currency} value={currency}>
                          {currency}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Game Filter */}
              {availableGames.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                    <PlayIcon className="w-4 h-4" />
                    {t('betHistory.gameFilter', 'Game Filter')}
                  </h3>
                  <div>
                    <select
                      value={gameFilter}
                      onChange={(e) => dispatchFilter({ type: 'SET_GAME_FILTER', payload: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      aria-label="Game filter"
                    >
                      <option value="all">{t('betHistory.allGames', 'All Games')} ({availableGames.length})</option>
                      {availableGames.map(game => (
                        <option key={game.id} value={game.id}>
                          {game.name}
                          {game.platform !== 'unknown' && ` (${game.platform})`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              
              {/* Active Filters Summary */}
              <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-gray-500 dark:text-gray-400">{t('betHistory.activeFilters', 'Active filters')}:</span>
                  {currencyFilter !== 'all' && (
                    <span className="px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full">
                      {currencyFilter}
                    </span>
                  )}
                  {amountFilter.type !== 'all' && (
                    <span className="px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full">
                      {amountFilter.type === 'win' ? t('betHistory.wins') : t('betHistory.losses')}
                    </span>
                  )}
                  {(amountFilter.min !== '' || amountFilter.max !== '') && (
                    <span className="px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full">
                      {amountFilter.min !== '' && amountFilter.max !== '' 
                        ? `${formatCurrency(amountFilter.min)} - ${formatCurrency(amountFilter.max)}`
                        : amountFilter.min !== '' 
                          ? `≥${formatCurrency(amountFilter.min)}`
                          : `≤${formatCurrency(amountFilter.max)}`
                      }
                    </span>
                  )}
                  {gameFilter !== 'all' && (
                    <span className="px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full">
                      {availableGames.find(g => g.id === gameFilter)?.name || gameFilter}
                    </span>
                  )}
                  {searchTerm.trim() && (
                    <span className="px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full flex items-center gap-1">
                      <MagnifyingGlassIcon className="w-3 h-3" />
                      "{searchTerm.trim()}"
                    </span>
                  )}
                </div>
              </div>
            </div>
            </>
          )}
        </div>

        {/* Auto-Loading Indicator */}
        {isAutoLoading && (
          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <ArrowPathIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 animate-spin" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  {t('betHistory.autoLoading', 'Auto-loading more results...')}
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
                  {t('betHistory.autoLoadingDesc', 'Finding more transactions that match your filters')}
                  {autoLoadAttempts > 0 && ` (${autoLoadAttempts}/25)`}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Controls Section */}
        <div className="flex flex-col gap-3 mb-6">
          {/* Tabs - Sport and Casino */}
          <div role="tablist" aria-label={t('betHistory.platformTabs', 'Platform selection')} className="flex gap-0.5 sm:gap-1 bg-gray-100 dark:bg-gray-800 p-0.5 sm:p-1 rounded-lg">
            <button
              role="tab"
              aria-selected={activeTab === 'sport'}
              aria-controls="transactions-panel"
              onClick={() => dispatchFilter({ type: 'SET_ACTIVE_TAB', payload: 'sport' })}
              className={`flex-1 px-3 py-2.5 sm:px-3 sm:py-2 rounded-md font-medium text-sm transition-all duration-150 ${
                activeTab === 'sport'
                  ? 'bg-primary-500 text-white shadow-sm'
                  : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <span className="flex items-center justify-center gap-0.5 sm:gap-2">
                <TrophyIcon className="w-3 h-3 sm:w-4 sm:h-4" aria-hidden="true" />
                {t('betHistory.sport', 'Sport')}
                {activeTab === 'sport' && displayedRounds.length > 0 && (
                  <span className={`px-0.5 sm:px-1 py-0 sm:py-0.5 text-[9px] sm:text-xs rounded bg-white/20 text-white`}>
                    {displayedRounds.length}
                  </span>
                )}
              </span>
            </button>
            <button
              role="tab"
              aria-selected={activeTab === 'casino'}
              aria-controls="transactions-panel"
              onClick={() => dispatchFilter({ type: 'SET_ACTIVE_TAB', payload: 'casino' })}
              className={`flex-1 px-3 py-2.5 sm:px-3 sm:py-2 rounded-md font-medium text-sm transition-all duration-150 ${
                activeTab === 'casino'
                  ? 'bg-primary-500 text-white shadow-sm'
                  : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <span className="flex items-center justify-center gap-0.5 sm:gap-2">
                <PlayIcon className="w-3 h-3 sm:w-4 sm:h-4" aria-hidden="true" />
                {t('betHistory.casino', 'Casino')}
                {activeTab === 'casino' && displayedRounds.length > 0 && (
                  <span className={`px-0.5 sm:px-1 py-0 sm:py-0.5 text-[9px] sm:text-xs rounded bg-white/20 text-white`}>
                    {displayedRounds.length}
                  </span>
                )}
              </span>
            </button>
          </div>

          {/* Sports Filter Buttons - Show only for sports tab */}
          {activeTab === 'sport' && (
            <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg">
              <button
                onClick={() => dispatchFilter({ type: 'SET_SPORTS_RESULT_FILTER', payload: 'all' })}
                className={`flex-1 px-3 py-2.5 sm:px-3 sm:py-2 rounded-md font-medium text-sm transition-all duration-150 flex items-center justify-center gap-1 ${
                  sportsResultFilter === 'all'
                    ? 'bg-primary-500 text-white shadow-sm'
                    : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <span>{t('common.all', 'All')}</span>
              </button>
              <button
                onClick={() => dispatchFilter({ type: 'SET_SPORTS_RESULT_FILTER', payload: 'open' })}
                className={`flex-1 px-3 py-2.5 sm:px-3 sm:py-2 rounded-md font-medium text-sm transition-all duration-150 flex items-center justify-center gap-1 ${
                  sportsResultFilter === 'open'
                    ? 'bg-yellow-500 text-white shadow-sm'
                    : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <ClockIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>{t('betHistory.open', 'Open')}</span>
              </button>
              <button
                onClick={() => dispatchFilter({ type: 'SET_SPORTS_RESULT_FILTER', payload: 'wins' })}
                className={`flex-1 px-3 py-2.5 sm:px-3 sm:py-2 rounded-md font-medium text-sm transition-all duration-150 flex items-center justify-center gap-1 ${
                  sportsResultFilter === 'wins'
                    ? 'bg-green-500 text-white shadow-sm'
                    : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <CheckCircleIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>{t('betHistory.wins', 'Wins')}</span>
              </button>
              <button
                onClick={() => dispatchFilter({ type: 'SET_SPORTS_RESULT_FILTER', payload: 'losses' })}
                className={`flex-1 px-3 py-2.5 sm:px-3 sm:py-2 rounded-md font-medium text-sm transition-all duration-150 flex items-center justify-center gap-1 ${
                  sportsResultFilter === 'losses'
                    ? 'bg-red-500 text-white shadow-sm'
                    : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <XCircleIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>{t('betHistory.losses', 'Losses')}</span>
              </button>
            </div>
          )}

          {/* Sort and Filters - Mobile optimized */}
          <div className="flex gap-1 sm:gap-2">
            {/* Sort Dropdown - Compact on mobile */}
            <select
              value={sortOrder}
              onChange={(e) => dispatchFilter({ type: 'SET_SORT_ORDER', payload: e.target.value })}
              className="px-3 py-2.5 sm:px-3 sm:py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm cursor-pointer min-h-[44px]"
              title={t('betHistory.sortOrder', 'Sort Order')}
            >
              <option value="newest">{t('betHistory.newest', 'Newest')}</option>
              <option value="oldest">{t('betHistory.oldest', 'Oldest')}</option>
              <option value="highestWin">{t('betHistory.highestWin', 'High Net')}</option>
              <option value="lowestWin">{t('betHistory.lowestWin', 'Low Net')}</option>
            </select>
          </div>
          
          {/* Currency Filter - Quick Access */}
          {availableCurrencies.length > 1 && (
            <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg overflow-x-auto">
              <button
                onClick={() => dispatchFilter({ type: 'SET_CURRENCY_FILTER', payload: 'all' })}
                className={`px-2 sm:px-3 py-2 rounded-md font-medium text-xs sm:text-sm transition-all duration-150 whitespace-nowrap ${
                  currencyFilter === 'all'
                    ? 'bg-primary-500 text-white shadow-sm'
                    : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                {t('betHistory.allCurrencies', 'All Currencies')}
              </button>
              {availableCurrencies.map(currency => (
                <button
                  key={currency}
                  onClick={() => dispatchFilter({ type: 'SET_CURRENCY_FILTER', payload: currency })}
                  className={`px-2 sm:px-3 py-2 rounded-md font-medium text-xs sm:text-sm transition-all duration-150 whitespace-nowrap ${
                    currencyFilter === currency
                      ? 'bg-primary-500 text-white shadow-sm'
                      : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  {currency}
                </button>
              ))}
            </div>
          )}
          
          {/* Provider Filter - Show only when providers are available */}
          {availableProviders.length > 0 && (
            <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg overflow-x-auto">
              <button
                onClick={() => dispatchFilter({ type: 'SET_PROVIDER_FILTER', payload: 'all' })}
                className={`px-2 sm:px-3 py-2 rounded-md font-medium text-xs sm:text-sm transition-all duration-150 whitespace-nowrap ${
                  providerFilter === 'all'
                    ? 'bg-primary-500 text-white shadow-sm'
                    : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                {t('betHistory.allProviders', 'All Providers')}
              </button>
              {availableProviders.map(provider => (
                <button
                  key={provider}
                  onClick={() => dispatchFilter({ type: 'SET_PROVIDER_FILTER', payload: provider })}
                  className={`px-2 sm:px-3 py-2 rounded-md font-medium text-xs sm:text-sm transition-all duration-150 whitespace-nowrap ${
                    providerFilter === provider
                      ? 'bg-primary-500 text-white shadow-sm'
                      : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  {provider}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Single-bet deep link banner */}
        {focusBetId && (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                {t('betHistory.focusedBet', 'Showing a single bet')}
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-300 break-all">
                {focusBetId}
              </p>
            </div>
            <button
              onClick={() => setFocusBetId(null)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-amber-100 dark:bg-amber-800/50 hover:bg-amber-200 dark:hover:bg-amber-800 text-amber-900 dark:text-amber-100 transition-colors"
            >
              <XMarkIcon className="w-4 h-4" />
              {t('betHistory.showAllBets', 'Show all bets')}
            </button>
          </div>
        )}

        {/* Balance Chart - Show when transactions exist (casino only) */}
        {displayedRounds.length > 0 && activeTab === 'casino' && (
          <div className="mb-4">
            <BalanceChart transactions={transactions} />
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className={`rounded-lg p-4 mb-6 flex items-start gap-3 ${
            error.includes(t('betHistory.tooManyPlayers').substring(0, 20))
              ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
              : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
          }`}>
            {error.includes(t('betHistory.tooManyPlayers').substring(0, 20)) ? (
              <UserGroupIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            ) : (
              <XCircleIcon className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <p className={`text-sm font-medium ${
                error.includes(t('betHistory.tooManyPlayers').substring(0, 20))
                  ? 'text-amber-800 dark:text-amber-300'
                  : 'text-red-600 dark:text-red-400'
              }`}>
                {error.includes(t('betHistory.tooManyPlayers').substring(0, 20))
                  ? t('betHistory.tooManyPlayersTitle', 'Too Many Players Selected')
                  : t('common.error', 'Error')}
              </p>
              <p className={`text-sm mt-1 ${
                error.includes(t('betHistory.tooManyPlayers').substring(0, 20))
                  ? 'text-amber-700 dark:text-amber-400'
                  : 'text-red-600 dark:text-red-400'
              }`}>
                {error}
              </p>
            </div>
          </div>
        )}

        {/* Data Range Summary - Show loaded data information */}
        {transactions.length > 0 && (
          <div className="mb-3 p-2.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex flex-col gap-1.5">
              <div className="flex flex-wrap items-center gap-2 text-xs text-blue-800 dark:text-blue-200">
                <CalendarIcon className="w-4 h-4" />
                <span className="font-medium">
                  {t('betHistory.dataLoaded', 'Data loaded')}:
                </span>
                <span>
                  {transactions.length} {t('betHistory.transactions', 'transactions')}
                </span>
                <span className="text-blue-600 dark:text-blue-400">•</span>
                <span>
                  {format(new Date(Math.min(...transactions.map(tx => getTransactionTimestamp(tx)))), 'MMM d, yyyy HH:mm')}
                  {' → '}
                  {format(new Date(Math.max(...transactions.map(tx => getTransactionTimestamp(tx)))), 'MMM d, yyyy HH:mm')}
                </span>
                {!hasMore && (
                  <>
                    <span className="text-blue-600 dark:text-blue-400">•</span>
                    <span className="font-medium text-green-700 dark:text-green-400">
                      ✓ {t('betHistory.allDataLoaded', 'Complete')}
                    </span>
                  </>
                )}
              </div>
              {/* Show time span and warning if data doesn't cover full range */}
              {transactions.length > 0 && !hasMore && (() => {
                const oldest = Math.min(...transactions.map(tx => getTransactionTimestamp(tx)));
                const newest = Math.max(...transactions.map(tx => getTransactionTimestamp(tx)));
                const spanHours = Math.round((newest - oldest) / (1000 * 60 * 60));
                const spanDays = Math.floor(spanHours / 24);
                const remainingHours = spanHours % 24;
                
                // Calculate expected time span
                let expectedDays = 7; // default
                switch (dateRange) {
                  case 'today': expectedDays = 0; break;
                  case 'last7': expectedDays = 7; break;
                  case 'last30': expectedDays = 30; break;
                  case 'all': expectedDays = 730; break;
                  case 'thisMonth': expectedDays = new Date().getDate(); break;
                  case 'custom': 
                    expectedDays = Math.ceil((customEndDate.getTime() - customStartDate.getTime()) / (1000 * 60 * 60 * 24));
                    break;
                }
                
                const actualDays = spanHours / 24;
                const isShortSpan = expectedDays > 1 && actualDays < (expectedDays * 0.2); // Less than 20% of expected
                
                return (
                  <div className="space-y-1">
                    <div className="text-[10px] text-blue-700 dark:text-blue-300 flex items-center gap-1">
                      <ClockIcon className="w-3 h-3" />
                      <span>
                        {t('betHistory.actualSpan', 'Actual data span')}: 
                        {spanDays > 0 && ` ${spanDays} ${t('betHistory.days', 'days')}`}
                        {remainingHours > 0 && ` ${remainingHours} ${t('betHistory.hours', 'hours')}`}
                        {spanHours === 0 && ' < 1 hour'}
                      </span>
                    </div>
                    {isShortSpan && (
                      <div className="text-[10px] text-amber-700 dark:text-amber-400 flex items-start gap-1">
                        <span>⚠️</span>
                        <span>
                          {t('betHistory.dataSpanWarning', 'Player has limited activity - data only covers part of the selected time range.')}
                          {' '}
                          ({t('betHistory.expected', 'Expected')}: {expectedDays} {t('betHistory.days', 'days')}, 
                          {' '}{t('betHistory.found', 'found')}: {Math.ceil(actualDays)} {t('betHistory.days', 'days')})
                        </span>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Active Filters Chips */}
        {((currencyFilter !== 'all') || 
          (providerFilter !== 'all') || 
          (gameFilter !== 'all') || 
          (amountFilter.type !== 'all') || 
          (amountFilter.min !== '') || 
          (amountFilter.max !== '') ||
          (sportsResultFilter !== 'all' && activeTab === 'sport')) && (
          <div className="mb-4 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {t('betHistory.activeFilters', 'Active filters')}:
              </span>
              
              {/* Currency filter chip */}
              {currencyFilter !== 'all' && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">
                  <BanknotesIcon className="w-3 h-3" />
                  {currencyFilter}
                  <button
                    onClick={() => dispatchFilter({ type: 'SET_CURRENCY_FILTER', payload: 'all' })}
                    className="hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5 transition-colors"
                    aria-label={`Remove ${currencyFilter} filter`}
                  >
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </span>
              )}
              
              {/* Provider filter chip */}
              {providerFilter !== 'all' && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-xs font-medium">
                  <BuildingStorefrontIcon className="w-3 h-3" />
                  {providerFilter}
                  <button
                    onClick={() => dispatchFilter({ type: 'SET_PROVIDER_FILTER', payload: 'all' })}
                    className="hover:bg-purple-200 dark:hover:bg-purple-800 rounded-full p-0.5 transition-colors"
                    aria-label={`Remove ${providerFilter} filter`}
                  >
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </span>
              )}
              
              {/* Game filter chip */}
              {gameFilter !== 'all' && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-xs font-medium">
                  <PlayIcon className="w-3 h-3" />
                  {availableGames.find(g => g.id === gameFilter)?.name || gameFilter}
                  <button
                    onClick={() => dispatchFilter({ type: 'SET_GAME_FILTER', payload: 'all' })}
                    className="hover:bg-green-200 dark:hover:bg-green-800 rounded-full p-0.5 transition-colors"
                    aria-label="Remove game filter"
                  >
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </span>
              )}
              
              {/* Amount type filter chip */}
              {amountFilter.type !== 'all' && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full text-xs font-medium">
                  {amountFilter.type === 'win' ? <CheckCircleIcon className="w-3 h-3" /> : <XCircleIcon className="w-3 h-3" />}
                  {amountFilter.type === 'win' ? t('betHistory.wins', 'Wins') : t('betHistory.losses', 'Losses')}
                  <button
                    onClick={() => dispatchFilter({ type: 'UPDATE_AMOUNT_FILTER', payload: { type: 'all' } })}
                    className="hover:bg-amber-200 dark:hover:bg-amber-800 rounded-full p-0.5 transition-colors"
                    aria-label="Remove result type filter"
                  >
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </span>
              )}
              
              {/* Amount range filter chip */}
              {(amountFilter.min !== '' || amountFilter.max !== '') && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full text-xs font-medium">
                  <BanknotesIcon className="w-3 h-3" />
                  {amountFilter.min !== '' && amountFilter.max !== '' 
                    ? `${formatCurrency(amountFilter.min)} - ${formatCurrency(amountFilter.max)}`
                    : amountFilter.min !== '' 
                      ? `≥${formatCurrency(amountFilter.min)}`
                      : `≤${formatCurrency(amountFilter.max)}`
                  }
                  <button
                    onClick={() => dispatchFilter({ type: 'UPDATE_AMOUNT_FILTER', payload: { min: '', max: '' } })}
                    className="hover:bg-amber-200 dark:hover:bg-amber-800 rounded-full p-0.5 transition-colors"
                    aria-label="Remove amount range filter"
                  >
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </span>
              )}
              
              {/* Sports result filter chip */}
              {sportsResultFilter !== 'all' && activeTab === 'sport' && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">
                  {sportsResultFilter === 'open' && <ClockIcon className="w-3 h-3" />}
                  {sportsResultFilter === 'wins' && <CheckCircleIcon className="w-3 h-3" />}
                  {sportsResultFilter === 'losses' && <XCircleIcon className="w-3 h-3" />}
                  {sportsResultFilter === 'open' ? t('betHistory.open', 'Open') : 
                   sportsResultFilter === 'wins' ? t('betHistory.wins', 'Wins') : 
                   t('betHistory.losses', 'Losses')}
                  <button
                    onClick={() => dispatchFilter({ type: 'SET_SPORTS_RESULT_FILTER', payload: 'all' })}
                    className="hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5 transition-colors"
                    aria-label="Remove sports result filter"
                  >
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </span>
              )}
              
              {/* Clear all filters button */}
              <button
                onClick={() => dispatchFilter({ type: 'CLEAR_ALL_FILTERS' })}
                className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                aria-label="Clear all filters"
              >
                <XMarkIcon className="w-3 h-3" />
                {t('betHistory.clearAllFilters', 'Clear all')}
              </button>
            </div>
          </div>
        )}

        {/* Results */}
        <div id="transactions-panel" role="tabpanel" aria-labelledby={activeTab === 'sport' ? 'sport-tab' : 'casino-tab'}>
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
            <div className="relative">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500"></div>
            </div>
            <p className="mt-4 text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
          </div>
        ) : effectivePlayers.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center">
            <UserIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              {t('betHistory.noPlayerSelected', 'No Player Selected')}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
              {t('betHistory.selectPlayersToView')}
            </p>
          </div>
        ) : displayedRounds.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center">
            <div className="text-gray-300 dark:text-gray-600 mx-auto mb-4">
              {activeTab === 'sport' ? (
                <TrophyIcon className="w-12 h-12 mx-auto" />
              ) : (
                <PlayIcon className="w-12 h-12 mx-auto" />
              )}
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              {activeTab === 'sport' && sportsResultFilter === 'open'
                ? t('betHistory.noOpenSportsBetsTitle', 'No Open Sports Bets')
                : activeTab === 'sport' && sportsResultFilter === 'wins'
                ? t('betHistory.noWinningSportsBetsTitle', 'No Winning Sports Bets')
                : activeTab === 'sport' && sportsResultFilter === 'losses'
                ? t('betHistory.noLosingSportsBetsTitle', 'No Losing Sports Bets')
                : activeTab === 'sport' 
                ? t('betHistory.noSportBetsTitle', 'No Sports Bets Found')
                : t('betHistory.noCasinoBetsTitle', 'No Casino Bets Found')
              }
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              {activeTab === 'sport' && sportsResultFilter === 'open'
                ? t('betHistory.noOpenSportsBets', 'No pending or open sports bets found')
                : activeTab === 'sport' && sportsResultFilter === 'wins'
                ? t('betHistory.noWinningSportsBets', 'No winning sports bets found in the last 2 months')
                : activeTab === 'sport' && sportsResultFilter === 'losses'
                ? t('betHistory.noLosingSportsBets', 'No losing sports bets found in the last 2 months')
                : activeTab === 'sport' 
                ? t('betHistory.noSportBets', 'No sports bets found in the last 2 months')
                : t('betHistory.noCasinoBets', 'No casino transactions found in the selected date range')
              }
            </p>

            {/* Deep Search Button - Show when amount filter is active and there's more data */}
            {activeTab === 'casino' && 
             hasMore && 
             !deepSearchActive && 
             !loading &&
             !loadingMore &&
             (amountFilter.min !== '' || amountFilter.max !== '' || amountFilter.type !== 'all') && (
              <div className="mt-6">
                <button
                  onClick={handleDeepSearch}
                  aria-label={t('betHistory.deepSearch', 'Deep Search')}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-all duration-150 shadow-md font-medium"
                >
                  <MagnifyingGlassIcon className="w-5 h-5" aria-hidden="true" />
                  <span>{t('betHistory.deepSearch', 'Deep Search')}</span>
                </button>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                  {t('betHistory.deepSearchHint', 'Click to search the entire time range for transactions matching your amount filter')}
                </p>
              </div>
            )}

            {/* Deep Search Progress - Show when deep search is active */}
            {deepSearchActive && (
              <div className="mt-6 max-w-md mx-auto">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {deepSearchProgress === 100 
                      ? t('betHistory.deepSearchComplete', 'Deep search complete')
                      : t('betHistory.deepSearching', 'Deep searching...')
                    }
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {Math.round(deepSearchProgress)}%
                    </span>
                    {deepSearchProgress < 100 && (
                      <button
                        onClick={handleCancelDeepSearch}
                        className="text-xs text-red-600 dark:text-red-400 hover:underline"
                        aria-label="Cancel deep search"
                      >
                        {t('common.cancel', 'Cancel')}
                      </button>
                    )}
                  </div>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
                  <div 
                    className={`h-2.5 rounded-full transition-all duration-300 ease-out ${
                      deepSearchProgress === 100 ? 'bg-green-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${deepSearchProgress}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                  {deepSearchProgress === 100 ? (
                    <>
                      {displayedRounds.length > 0 ? (
                        <>
                          {t('betHistory.deepSearchFoundResults', 'Deep search complete - found {{count}} matching results', { count: displayedRounds.length })}
                          <br />
                          <span className="text-[10px] mt-1 block text-green-600 dark:text-green-400">
                            {t('betHistory.entireTimeframeScanned', 'Scanned entire timeframe: {{total}} transactions', { total: transactions.length })}
                          </span>
                        </>
                      ) : (
                        <>
                          {t('betHistory.deepSearchScanned', 'Scanned {{count}} transactions - no matches found', { count: transactions.length })}
                          <br />
                          <span className="text-[10px] mt-1 block">
                            {t('betHistory.tryAdjustingFilters', 'Try adjusting your amount filter or expanding the date range')}
                          </span>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {autoLoadAttempts > 0 && totalCount > 0 ? (
                        <>
                          Load {autoLoadAttempts} of 50 • {transactions.length}/{totalCount} transactions
                          {totalCount > 0 && ` (${Math.round((transactions.length / totalCount) * 100)}%)`}
                          {displayedRounds.length > 0 && (
                            <span className="text-green-600 dark:text-green-400 font-medium"> • {displayedRounds.length} matches</span>
                          )}
                          <br />
                        </>
                      ) : autoLoadAttempts > 0 ? (
                        <>
                          Load {autoLoadAttempts} of 50 • {transactions.length} transactions scanned
                          {displayedRounds.length > 0 && (
                            <span className="text-green-600 dark:text-green-400 font-medium"> • {displayedRounds.length} matches</span>
                          )}
                          <br />
                        </>
                      ) : null}
                      {t('betHistory.deepSearchProgress', 'Loading entire timeframe to find all matching transactions...')}
                      {totalCount > 0 && (
                        <>
                          <br />
                          <span className="text-[10px] text-gray-400 dark:text-gray-500">
                            {totalCount} total transactions in date range
                          </span>
                        </>
                      )}
                    </>
                  )}
                </p>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Load More Button at TOP when sorting by oldest (casino only) */}
            {activeTab === 'casino' && sortOrder === 'oldest' && hasMore && (
              <div className="mb-6 text-center space-y-3">
                {totalCount > 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {visibleRoundsCount < displayedRounds.length 
                      ? `${t('betHistory.displaying', 'Displaying')} ${visibleRoundsCount} ${t('common.of', 'of')} ${displayedRounds.length} ${t('betHistory.rounds', 'rounds')} ${t('common.from', 'from')} ${totalCount} ${t('betHistory.transactions', 'transactions')}`
                      : `${t('betHistory.showing', 'Showing')} ${displayedRounds.length} ${t('betHistory.rounds', 'rounds')} ${t('common.from', 'from')} ${totalCount} ${t('betHistory.transactions', 'transactions')}`
                    }
                  </p>
                )}
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="inline-flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150 shadow-md font-medium"
                >
                  {loadingMore ? (
                    <>
                      <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      <span className="text-sm sm:text-base">
                        {deepSearchActive ? 
                          t('betHistory.deepSearching', 'Deep searching...') :
                          isAutoLoading ? 
                          t('betHistory.autoLoading', 'Auto-loading...') : 
                          t('common.loading', 'Loading...')
                        }
                      </span>
                    </>
                  ) : (
                    <>
                      <ArrowDownIcon className="w-4 h-4" />
                      <span className="text-sm sm:text-base">{t('common.loadOlder')}</span>
                    </>
                  )}
                </button>
              </div>
            )}
            
            {/* Total Net Summary - Only when all data is loaded and not loading */}
            {totalNetByCurrency && totalNetByCurrency.length > 0 && (
              <div className="mb-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {t('betHistory.totalNet', 'Total Net')}
                  </span>
                  <span className="text-[10px] text-gray-400 dark:text-gray-500">
                    {displayedRounds.length} {t('betHistory.rounds', 'rounds')}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {totalNetByCurrency.map(({ currency, total }) => {
                    const isPositive = total > 0;
                    const isNeutral = total === 0;
                    return (
                      <div 
                        key={currency}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600"
                      >
                        <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase">
                          {currency}
                        </span>
                        <span className={`text-sm font-semibold ${
                          isPositive 
                            ? 'text-green-600 dark:text-green-400' 
                            : isNeutral
                            ? 'text-gray-600 dark:text-gray-400'
                            : 'text-red-600 dark:text-red-400'
                        }`}>
                          {total > 0 ? '+' : ''}{formatCurrency(total, currency)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Deep Search Progress - Show inline when deep search is loading full dataset (e.g. for net sorting) */}
            {deepSearchActive && (
              <div className="mb-4 max-w-lg mx-auto">
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                      {deepSearchProgress === 100 
                        ? t('betHistory.deepSearchComplete', 'Deep search complete')
                        : (sortOrder === 'highestWin' || sortOrder === 'lowestWin')
                        ? t('betHistory.loadingFullDataset', 'Loading full dataset for accurate sorting...')
                        : t('betHistory.deepSearching', 'Deep searching...')
                      }
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-blue-500 dark:text-blue-400">
                        {Math.round(deepSearchProgress)}%
                      </span>
                      {deepSearchProgress < 100 && (
                        <button
                          onClick={handleCancelDeepSearch}
                          className="text-xs text-red-600 dark:text-red-400 hover:underline"
                          aria-label="Cancel deep search"
                        >
                          {t('common.cancel', 'Cancel')}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="w-full bg-blue-100 dark:bg-blue-900/40 rounded-full h-1.5 overflow-hidden">
                    <div 
                      className={`h-1.5 rounded-full transition-all duration-300 ease-out ${
                        deepSearchProgress === 100 ? 'bg-green-500' : 'bg-blue-500'
                      }`}
                      style={{ width: `${deepSearchProgress}%` }}
                    />
                  </div>
                  {totalCount > 0 && deepSearchProgress < 100 && (
                    <p className="text-[10px] text-blue-500 dark:text-blue-400 mt-1.5 text-center">
                      {transactions.length}/{totalCount} {t('betHistory.transactions', 'transactions')}
                      {displayedRounds.length > 0 && ` • ${displayedRounds.length} ${t('betHistory.rounds', 'rounds')}`}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Round Grid - Single column on mobile for better readability */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 sm:gap-3">
              {visibleRounds.map((round) => (
                <RoundCard
                  key={round.roundId || round.transactions[0].transactionId}
                  round={round}
                  isFromOpenBetsTab={false}
                  userTree={userTree}
                  isFocused={Boolean(focusBetId)}
                  canSetLimits={canSetLimits}
                />
              ))}
            </div>
            
            {/* Show More Rounds Button (frontend pagination) */}
            {visibleRoundsCount < displayedRounds.length && (
              <div className="mt-6 text-center">
                {/* Loading spinner for infinite scroll */}
                {loadingMoreRounds ? (
                  <div className="py-4">
                    <div className="inline-flex items-center gap-2 text-gray-500 dark:text-gray-400">
                      <ArrowPathIcon className="w-5 h-5 animate-spin" />
                      <span className="text-sm">{t('betHistory.loadingMoreRounds', 'Loading more rounds...')}</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={handleShowMoreRounds}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors duration-150 font-medium"
                    >
                      <ChevronDownIcon className="w-4 h-4" />
                      <span className="text-sm">
                        {t('betHistory.showMoreRounds', 'Show More')} 
                        {' '}({displayedRounds.length - visibleRoundsCount} {t('betHistory.remaining', 'remaining')})
                      </span>
                    </button>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      {t('betHistory.showing', 'Showing')} {visibleRoundsCount} {t('common.of', 'of')} {displayedRounds.length} {t('betHistory.rounds', 'rounds')}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 italic">
                      {t('betHistory.scrollToLoadMore', 'or scroll down to load more automatically')}
                    </p>
                  </>
                )}
              </div>
            )}
            
            {/* Load More Button at BOTTOM for newest and net result sorting (casino only) */}
            {activeTab === 'casino' && sortOrder !== 'oldest' && (
              <div className="mt-8 text-center space-y-3">
                {totalCount > 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {visibleRoundsCount < displayedRounds.length 
                      ? `${t('betHistory.displaying', 'Displaying')} ${visibleRoundsCount} ${t('common.of', 'of')} ${displayedRounds.length} ${t('betHistory.rounds', 'rounds')} ${t('common.from', 'from')} ${totalCount} ${t('betHistory.transactions', 'transactions')}`
                      : `${t('betHistory.showing', 'Showing')} ${displayedRounds.length} ${t('betHistory.rounds', 'rounds')} ${t('common.from', 'from')} ${totalCount} ${t('betHistory.transactions', 'transactions')}`
                    }
                  </p>
                )}
                {hasMore && (
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="inline-flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150 shadow-md font-medium"
                  >
                    {loadingMore ? (
                      <>
                        <ArrowPathIcon className="w-4 h-4 animate-spin" />
                        <span className="text-sm sm:text-base">
                          {deepSearchActive ? 
                            t('betHistory.deepSearching', 'Deep searching...') :
                            isAutoLoading ? 
                            t('betHistory.autoLoading', 'Auto-loading...') : 
                            t('common.loading', 'Loading...')
                          }
                        </span>
                      </>
                    ) : (
                      <>
                        <ChevronDownIcon className="w-4 h-4" />
                        <span className="text-sm sm:text-base">{t('common.loadMore')}</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
          </>
        )}
        </div>
      </div>
    </div>
  );
};

export default BetHistory; 